import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '@/components/Layout';
import { DimensionInput } from '@/components/DimensionInput';
import { useAuth } from '@/context/AuthContext';
import { quotesApi, SavedQuote, ordersApi, freightApi } from '@/services/api';
import { customProductsApi, CustomProduct } from '@/services/api';
import clsx from 'clsx';
import { ShippingDisclaimer } from '@/components/ShippingDisclaimer';

// ─── Pricing Matrices (same as QuoteCalculatorPage) ───────────────────────────

const PRICING_MATRICES = {
  cvw: {
    productType: 'cvw',
    heights: [84, 96, 108, 120, 132, 144, 156, 168, 180, 192],
    widths: [48, 60, 72, 84, 96, 108, 120, 132, 144, 156, 168, 180, 192, 204, 216, 228, 240, 252, 264, 276, 288],
    prices: {
      84: [3988.6, 4111.96, 4239.13, 4370.24, 4505.4, 4644.74, 4788.4, 4936.49, 5089.17, 5246.56, 5408.83, 5576.11, 5748.57, 5926.36, 6109.65, 6298.61, 6493.41, 6694.24, 6901.27, 7114.72, 7334.76],
      96: [4107.4, 4234.43, 4365.39, 4500.41, 4639.59, 4783.09, 4931.05, 5083.62, 5240.94, 5403.15, 5570.39, 5742.81, 5920.56, 6103.77, 6291.62, 6486.21, 6686.81, 6893.62, 7106.83, 7326.63, 7553.22],
      108: [4230.6, 4361.44, 4496.33, 4635.4, 4778.76, 4926.55, 5078.92, 5235.99, 5397.99, 5565.06, 5737.35, 5914.99, 6098.14, 6286.93, 6480.34, 6680.76, 6887.38, 7100.39, 7319.99, 7546.39, 7779.78],
      120: [4358.2, 4492.99, 4631.95, 4775.2, 4922.89, 5075.15, 5232.13, 5394.01, 5560.99, 5733.23, 5910.87, 6094.06, 6282.96, 6477.71, 6677.35, 6882.99, 7095.11, 7314.55, 7540.77, 7773.99, 8014.43],
      132: [4489.1, 4627.94, 4771.07, 4918.63, 5070.75, 5227.58, 5389.27, 5555.98, 5727.85, 5905.05, 6087.73, 6276.03, 6470.12, 6670.01, 6876.3, 7088.97, 7308.22, 7534.25, 7767.26, 8007.49, 8255.14],
      144: [4623.3, 4766.29, 4913.7, 5065.67, 5222.34, 5383.86, 5550.37, 5722.02, 5898.96, 6081.35, 6269.33, 6463.05, 6662.66, 6868.29, 7081.87, 7300.89, 7526.7, 7759.48, 7999.46, 8246.87, 8501.93],
      156: [4761.9, 4909.18, 5061.01, 5217.53, 5378.9, 5545.26, 5716.75, 5893.54, 6075.77, 6263.59, 6457.13, 6656.55, 6862.01, 7073.64, 7294.17, 7519.76, 7752.33, 7992.1, 8239.28, 8494.1, 8756.8],
      168: [4904.9, 5056.6, 5212.99, 5374.21, 5540.43, 5711.78, 5888.41, 6070.5, 6258.16, 6451.55, 6650.8, 6856.07, 7067.51, 7285.25, 7513.22, 7745.58, 7985.14, 8232.1, 8486.7, 8749.18, 9019.77],
      180: [5052.3, 5208.56, 5369.65, 5535.72, 5706.93, 5883.43, 6065.38, 6252.92, 6446.19, 6645.35, 6850.54, 7061.9, 7279.58, 7503.7, 7739, 7978.35, 8225.1, 8479.49, 8741.74, 9012.1, 9290.83],
      192: [5204.1, 5365.05, 5530.98, 5702.04, 5878.39, 6060.2, 6247.61, 6440.78, 6639.85, 6845.06, 7056.52, 7274.39, 7498.81, 7729.93, 7971.52, 8218.07, 8472.23, 8734.26, 9004.39, 9282.88, 9569.98]
    }
  },
  solar99: {
    productType: 'solar99',
    heights: [84, 96, 108, 120, 132, 144, 156, 168, 180, 192],
    widths: [48, 60, 72, 84, 96, 108, 120, 132, 144, 156, 168, 180, 192, 204, 216, 228, 240, 252, 264, 276, 288],
    prices: {
      84: [3438.44, 3544.78, 3654.41, 3767.43, 3884.00, 4003.07, 4127.91, 4255.58, 4387.19, 4522.89, 4662.77, 4806.97, 4955.64, 5108.91, 5266.92, 5430.81, 5599.75, 5773.87, 5953.35, 6138.36, 6329.05],
      96: [3546.01, 3666.71, 3790.80, 3918.40, 4049.59, 4184.50, 4323.25, 4465.96, 4612.72, 4763.70, 4919.00, 5078.75, 5243.12, 5412.23, 5586.23, 5765.26, 5949.49, 6139.09, 6334.21, 6535.01, 6741.70],
      108: [3617.67, 3729.56, 3844.90, 3963.82, 4086.41, 4212.80, 4343.09, 4477.40, 4615.85, 4758.58, 4905.71, 5057.36, 5213.67, 5374.80, 5540.47, 5712.85, 5890.53, 6074.69, 6263.47, 6458.07, 6658.64],
      120: [3725.73, 3840.90, 3959.55, 4082.40, 4208.40, 4338.60, 4473.00, 4611.60, 4754.40, 4901.40, 5052.60, 5209.05, 5369.70, 5536.65, 5707.80, 5884.20, 6065.85, 6253.80, 6447.00, 6646.50, 6852.30],
      132: [3796.49, 3913.91, 4034.95, 4159.74, 4288.40, 4421.03, 4557.77, 4698.77, 4844.14, 4994.04, 5148.59, 5307.94, 5472.23, 5641.58, 5815.37, 5995.23, 6180.64, 6371.80, 6568.87, 6772.02, 6981.47],
      144: [3959.34, 4081.79, 4208.03, 4338.18, 4472.35, 4610.68, 4753.30, 4900.34, 5051.95, 5208.26, 5369.41, 5535.52, 5706.75, 5883.23, 6064.83, 6252.40, 6446.77, 6646.12, 6851.65, 7063.52, 7280.95],
      156: [4345.11, 4479.50, 4617.04, 4758.86, 4905.11, 5055.90, 5211.38, 5371.68, 5536.95, 5707.34, 5883.00, 6064.06, 6250.70, 6443.09, 6641.74, 6846.60, 7058.80, 7277.59, 7502.13, 7733.65, 7970.35],
      168: [4457.15, 4595.00, 4736.11, 4880.61, 5028.65, 5180.37, 5335.89, 5495.38, 5658.99, 5826.89, 5999.21, 6176.14, 6357.86, 6544.54, 6736.36, 6933.51, 7136.19, 7344.61, 7559.00, 7779.49, 8006.38],
      180: [4529.07, 4669.20, 4815.67, 4964.61, 5118.15, 5276.45, 5439.64, 5607.87, 5781.31, 5960.12, 6144.45, 6334.48, 6529.39, 6730.37, 6936.63, 7148.25, 7365.54, 7588.68, 7817.88, 8053.34, 8295.32],
      192: [4644.26, 4787.90, 4935.97, 5088.62, 5246.01, 5408.25, 5575.52, 5747.96, 5925.74, 6109.01, 6297.94, 6492.73, 6693.53, 6900.55, 7113.97, 7333.98, 7559.81, 7792.65, 8031.73, 8277.25, 8530.46]
    }
  },
  solar94: {
    productType: 'solar94',
    heights: [84, 96, 108, 120, 132, 144, 156, 168, 180, 192],
    widths: [48, 60, 72, 84, 96, 108, 120, 132, 144, 156, 168, 180, 192, 204, 216, 228, 240, 252, 264, 276, 288],
    prices: {
      84: [3274.70, 3375.98, 3480.39, 3588.03, 3699.00, 3813.40, 3931.34, 4052.93, 4178.28, 4307.51, 4440.73, 4578.07, 4719.66, 4865.63, 5016.11, 5171.25, 5331.19, 5496.07, 5666.05, 5841.29, 6021.95],
      96: [3378.10, 3482.58, 3590.29, 3701.33, 3815.80, 3933.81, 4055.48, 4180.91, 4310.21, 4443.52, 4580.95, 4722.62, 4868.69, 5019.26, 5174.50, 5334.53, 5499.52, 5669.61, 5844.96, 6025.73, 6212.09],
      108: [3445.40, 3551.96, 3661.81, 3775.07, 3891.82, 4012.19, 4136.27, 4264.19, 4396.05, 4531.98, 4672.10, 4816.53, 4965.40, 5118.86, 5277.59, 5440.81, 5609.08, 5782.56, 5961.40, 6145.78, 6335.85],
      120: [3548.60, 3658.00, 3771.00, 3888.00, 4008.00, 4132.00, 4260.00, 4392.00, 4528.00, 4668.00, 4812.00, 4961.00, 5114.00, 5273.00, 5436.00, 5604.00, 5777.00, 5956.00, 6140.00, 6330.00, 6526.00],
      132: [3615.70, 3727.53, 3842.81, 3961.66, 4084.19, 4210.50, 4340.74, 4475.02, 4613.47, 4756.23, 4903.42, 5055.18, 5211.64, 5372.94, 5538.45, 5709.74, 5886.33, 6068.38, 6256.06, 6449.55, 6649.02],
      144: [3770.80, 3887.42, 4007.65, 4131.60, 4259.38, 4391.12, 4526.95, 4666.99, 4811.38, 4960.25, 5113.72, 5271.92, 5435.00, 5603.08, 5776.03, 5954.67, 6138.83, 6328.69, 6524.43, 6726.21, 6934.24],
      156: [4138.20, 4266.19, 4398.13, 4534.15, 4674.39, 4818.95, 4967.98, 5121.60, 5279.95, 5443.18, 5611.42, 5784.82, 5963.53, 6147.71, 6338.80, 6534.85, 6736.96, 6945.32, 7160.12, 7381.57, 7609.86],
      168: [4244.90, 4376.19, 4511.53, 4651.06, 4794.91, 4943.21, 5096.09, 5253.70, 5416.19, 5583.70, 5756.39, 5934.42, 6117.96, 6307.18, 6502.24, 6703.34, 6910.66, 7124.39, 7344.74, 7571.89, 7806.08],
      180: [4315.30, 4448.76, 4586.35, 4728.20, 4874.43, 5025.19, 5180.61, 5340.83, 5506.01, 5676.30, 5851.86, 6032.84, 6219.42, 6411.78, 6610.08, 6814.52, 7025.27, 7242.55, 7466.55, 7697.47, 7935.54],
      192: [4423.10, 4559.90, 4700.92, 4846.31, 4996.20, 5150.72, 5310.02, 5474.25, 5643.56, 5818.10, 5998.04, 6183.55, 6374.79, 6571.95, 6775.21, 6984.75, 7200.77, 7423.48, 7653.07, 7889.76, 8133.77]
    }
  },
  insect: {
    productType: 'insect',
    heights: [84, 96, 108, 120, 132, 144, 156, 168, 180, 192],
    widths: [48, 60, 72, 84, 96, 108, 120, 132, 144, 156, 168, 180, 192, 204, 216, 228, 240, 252, 264, 276, 288],
    prices: {
      84: [3158.10, 3255.77, 3356.47, 3460.28, 3567.29, 3677.62, 3791.36, 3908.62, 4029.51, 4154.13, 4282.61, 4415.06, 4551.61, 4692.38, 4837.51, 4987.12, 5141.36, 5300.37, 5464.30, 5633.30, 5807.53],
      96: [3256.00, 3356.70, 3460.52, 3567.54, 3677.88, 3791.63, 3908.89, 4029.79, 4154.42, 4282.91, 4415.37, 4551.93, 4692.71, 4837.84, 4987.47, 5141.72, 5300.74, 5464.68, 5633.69, 5807.93, 5987.56],
      108: [3317.60, 3420.21, 3525.99, 3635.04, 3747.46, 3863.36, 3982.85, 4106.03, 4233.02, 4363.94, 4498.90, 4638.04, 4781.49, 4929.37, 5081.83, 5238.99, 5401.03, 5568.07, 5740.28, 5917.81, 6100.84],
      120: [3414.40, 3520.00, 3628.87, 3741.10, 3856.80, 3976.09, 4099.06, 4225.83, 4356.53, 4491.27, 4630.17, 4773.37, 4921.00, 5073.20, 5230.10, 5391.86, 5558.62, 5730.53, 5907.76, 6090.48, 6278.84],
      132: [3474.90, 3582.37, 3693.17, 3807.39, 3925.14, 4046.54, 4171.69, 4300.71, 4433.72, 4570.85, 4712.21, 4857.95, 5008.20, 5163.09, 5322.77, 5487.40, 5657.11, 5832.07, 6012.44, 6198.40, 6390.10],
      144: [3624.50, 3736.60, 3852.16, 3971.30, 4094.13, 4220.75, 4351.29, 4485.86, 4624.60, 4767.63, 4915.08, 5067.09, 5223.81, 5385.37, 5551.93, 5723.64, 5900.66, 6083.15, 6271.29, 6465.25, 6665.20],
      156: [3998.60, 4122.27, 4249.76, 4381.20, 4516.70, 4656.39, 4800.40, 4948.87, 5101.93, 5259.72, 5422.39, 5590.09, 5762.98, 5941.22, 6124.97, 6314.40, 6509.69, 6711.02, 6918.58, 7132.55, 7353.15],
      168: [4089.80, 4216.29, 4346.69, 4481.12, 4619.71, 4762.59, 4909.89, 5061.74, 5218.29, 5379.68, 5546.06, 5717.59, 5894.42, 6076.72, 6264.66, 6458.42, 6658.16, 6864.08, 7076.38, 7295.23, 7520.86],
      180: [4154.70, 4283.20, 4415.67, 4552.23, 4693.02, 4838.17, 4987.80, 5142.06, 5301.10, 5465.05, 5634.07, 5808.32, 5987.96, 6173.15, 6364.08, 6560.90, 6763.82, 6973.01, 7188.67, 7410.99, 7640.20],
      192: [4255.90, 4387.53, 4523.22, 4663.12, 4807.34, 4956.02, 5109.30, 5267.31, 5430.22, 5598.17, 5771.31, 5949.80, 6133.81, 6323.52, 6519.09, 6720.71, 6928.57, 7142.86, 7363.77, 7591.52, 7826.30]
    }
  }
} as const;

const UCHANNEL_RATES = { MSRP: 12.75, DEALER: 9.16 };
const REMOTE_PRICING: Record<string, Record<string, number>> = {
  MSRP: {
    'alpha_pioneer_1ch': 125, 'alpha_pioneer_4ch': 200, 'alpha_pioneer_8ch_lcd': 275, 'alpha_pioneer_16ch_lcd': 350,
    'keder_remote_15ch': 200,
  },
  DEALER: {
    'alpha_pioneer_1ch': 77, 'alpha_pioneer_4ch': 82.5, 'alpha_pioneer_8ch_lcd': 143, 'alpha_pioneer_16ch_lcd': 165,
    'keder_remote_15ch': 82.5,
  },
};

const PRODUCT_LABELS: Record<string, string> = {
  solar94: 'Solar',
  solar99: 'Premium Solar',
  cvw: 'Clear Vinyl Windows',
  insect: 'Insect',
};

const MIN_WIDTH = 48, MIN_HEIGHT = 84, MAX_WIDTH = 288, MAX_HEIGHT = 192;
// Hard manufacturing floor — we don't build screens narrower than 30" (distinct
// from the 48" minimum pricing bucket). Mirrors NewOrderPage's submit rule.
const MIN_BUILD_WIDTH = 30;

// Motor Package Options (matches NewOrderPage)
const MOTOR_PACKAGES = [
  { value: 'none', label: 'No Motor', description: 'Screen only - reusing existing motor', badge: null },
  { value: 'keder', label: 'Keder Screens', description: 'Keder motor with Keder remotes', badge: 'Default' },
  { value: 'alpha', label: 'Alpha Package', description: 'Alpha motor with Alpha remotes', badge: '+$125/unit' },
  { value: 'somfy', label: 'Somfy Package', description: 'Somfy motor with Somfy remotes', badge: null },
];

const ALPHA_REMOTES = [
  { value: 'none', label: 'None' },
  { value: 'alpha_pioneer_1ch', label: 'Alpha Pioneer 1-ch' },
  { value: 'alpha_pioneer_4ch', label: 'Alpha Pioneer 4-ch' },
  { value: 'alpha_pioneer_8ch_lcd', label: 'Alpha Pioneer 8-ch LCD' },
  { value: 'alpha_pioneer_16ch_lcd', label: 'Alpha Pioneer 16-ch LCD' },
];

const SOMFY_REMOTES = [
  { value: 'none', label: 'None' },
  { value: 'somfy_situo_1ch', label: 'Somfy Situo 1-ch' },
  { value: 'somfy_situo_5ch', label: 'Somfy Situo 5-ch' },
  { value: 'somfy_telis_1ch', label: 'Somfy Telis 1-ch' },
  { value: 'somfy_telis_4ch', label: 'Somfy Telis 4-ch' },
  { value: 'somfy_telis_16ch', label: 'Somfy Telis 16-ch' },
];

const KEDER_REMOTES = [
  { value: 'none', label: 'None' },
  { value: 'keder_remote_15ch', label: 'Keder 15-ch Remote' },
];

const ALPHA_WALL_SWITCHES = [
  { value: 'none', label: 'None' },
  { value: 'alpha_rts_wall_switch', label: 'Alpha RTS Wall Switch' },
];

const SOMFY_WALL_SWITCHES = [
  { value: 'none', label: 'None' },
  { value: 'somfy_rts_wall_switch', label: 'Somfy RTS Wall Switch' },
];

const KEDER_WALL_SWITCHES = [
  { value: 'none', label: 'None' },
  { value: 'keder_wall_switch', label: 'Keder Wall Switch' },
];

const WALL_SWITCH_PRICE = 82.5;

// Remote / wall-switch options are package-specific. Switching motor packages
// used to blanket-reset both to 'none' while LEAVING the quantities alone —
// so a dealer who set "2 remotes" and then touched a motor tile ended up with
// qty 2 / type none. That prices at $0 and the estimate silently dropped the
// line. (Caught Aug 17 2026 on DES, Inc. "Duck Club 2" — two remotes missing
// from the PDF.) Keep the current pick when it's valid for the new package,
// otherwise fall back to that package's default rather than to nothing.
const remotesForPackage = (pkg: string) =>
  pkg === 'alpha' ? ALPHA_REMOTES : pkg === 'somfy' ? SOMFY_REMOTES : KEDER_REMOTES;
const wallSwitchesForPackage = (pkg: string) =>
  pkg === 'alpha' ? ALPHA_WALL_SWITCHES : pkg === 'somfy' ? SOMFY_WALL_SWITCHES : KEDER_WALL_SWITCHES;

/** First real (non-'none') option for a package — used when the old pick doesn't carry over. */
const defaultOptionFor = (options: { value: string }[]) =>
  options.find(o => o.value !== 'none')?.value || 'none';

/** Keep `current` if the new package offers it, else that package's default. */
const carryOverOption = (current: string, options: { value: string }[]) =>
  options.some(o => o.value === current && o.value !== 'none')
    ? current
    : defaultOptionFor(options);
// Wall switch went $45 -> $82.50 on this date. A saved quote created before it
// (and still within its 30-day validity) is honored at the old $45 price.
const WALL_SWITCH_OLD_PRICE = 45;
const WALL_SWITCH_PRICE_CHANGE = new Date('2026-07-16T00:00:00');
function wallSwitchPriceForQuote(loadedQuoteCreatedAt: string | null): number {
  if (loadedQuoteCreatedAt) {
    const created = new Date(loadedQuoteCreatedAt);
    const ageDays = (Date.now() - created.getTime()) / 86_400_000;
    if (created < WALL_SWITCH_PRICE_CHANGE && ageDays <= 30) return WALL_SWITCH_OLD_PRICE;
  }
  return WALL_SWITCH_PRICE;
}
const SOMFY_UPGRADE_PER_UNIT = 275;
const ALPHA_UPGRADE_PER_UNIT = 125;

const SUGGESTED_CATEGORIES = [
  'Interior Screens', 'TVs & Audio', 'Heaters & Fire Pits', 'Pool & Spa',
  'Lighting', 'Furniture', 'Awnings & Shading', 'Installation', 'Accessories', 'Other'
];

// ─── Types ──────────────────────────────────────────────────────────────────────

interface ScreenItem {
  id: number;
  width: number;
  height: number;
  uChannel: boolean;
  windowPanel?: boolean;     // include vinyl window panel on this screen
  windowCount?: number;      // number of windows; first is included, +$175 each after (matches NewOrder)
  customerPrice?: number;    // override customer price
}

// Fabric type catalog: dealer picks the actual fabric (Omega Pro 5%, 502 Vinyl, etc.).
// Pricing matrix is set automatically from the fabric's pricingCategory.
type FabricOption = { value: string; label: string; pricingCategory: keyof typeof PRICING_MATRICES };
const FABRIC_TYPE_GROUPS: { group: string; options: FabricOption[] }[] = [
  { group: 'Solar', options: [
    { value: 'omega_pro_3', label: 'Omega Pro 3%', pricingCategory: 'solar94' },
    { value: 'omega_pro_5', label: 'Omega Pro 5%', pricingCategory: 'solar94' },
    { value: 'omega_pro_10', label: 'Omega Pro 10%', pricingCategory: 'solar94' },
    { value: 'twitchell_80', label: 'Twitchell 80', pricingCategory: 'solar94' },
    { value: 'twitchell_90', label: 'Twitchell 90', pricingCategory: 'solar94' },
    { value: 'twitchell_95', label: 'Twitchell 95', pricingCategory: 'solar94' },
  ]},
  { group: 'Solar Premium', options: [
    { value: 'twitchell_nano_95', label: 'Twitchell Nano 95', pricingCategory: 'solar99' },
    { value: 'twitchell_nano_97', label: 'Twitchell Nano 97', pricingCategory: 'solar99' },
    { value: 'twitchell_nano_99', label: 'Twitchell Nano 99', pricingCategory: 'solar99' },
    { value: 'twitchell_dimout_blackout', label: 'Twitchell Dimout', pricingCategory: 'solar99' },
  ]},
  { group: 'Vinyl', options: [
    { value: '502_vinyl', label: '502 Vinyl', pricingCategory: 'cvw' },
  ]},
  { group: 'Insect', options: [
    { value: 'twitchell_nano_55_insect', label: 'Twitchell Nano 55 Insect', pricingCategory: 'insect' },
  ]},
];
const FABRIC_FLAT: FabricOption[] = FABRIC_TYPE_GROUPS.flatMap(g => g.options);

// Fabric colors per fabric type (cosmetic only — does not affect price).
const COLORS_BY_FABRIC: Record<string, string[]> = {
  omega_pro_3: ['Brown/Taupe', 'White', 'White/Linen', 'White Gray', 'Light Gray', 'Black/Bronze', 'Gray/Black', 'Black', 'Charcoal/Taupe'],
  omega_pro_5: ['Brown/Taupe', 'White', 'White/Linen', 'White Gray', 'Light Gray', 'Black/Bronze', 'Gray/Black', 'Black', 'Charcoal/Taupe'],
  omega_pro_10: ['Brown/Taupe', 'White', 'White/Linen', 'White Gray', 'Light Gray', 'Black/Bronze', 'Gray/Black', 'Black', 'Charcoal/Taupe'],
  twitchell_80: ['Black'],
  twitchell_90: ['Black'],
  twitchell_95: ['Black'],
  twitchell_nano_95: ['Black'],
  twitchell_nano_97: ['Black'],
  twitchell_nano_99: ['Black'],
  twitchell_dimout_blackout: ['Black'],
  '502_vinyl': ['Black', 'Red (Poppy)', 'White', 'Blue (Midnight)', 'Anthracite', 'Grey (Concrete)', 'Boulder', 'Antelope', 'Tan (Pepper)', 'Hemp'],
  twitchell_nano_55_insect: ['Standard'],
};

// Per-extra-window upcharge (first window is included in screen price)
const EXTRA_WINDOW_PRICE = 175;

interface CustomLineItem {
  id: number;
  name: string;
  category: string;
  description: string;
  quantity: number;
  unitCost: number;
  customerPrice: number;
  catalogProductId?: string;
}

interface DiscountItem {
  id: number;
  name: string;
  type: 'percent' | 'fixed' | 'per_screen';
  value: number;
  appliesTo: 'all' | 'screens' | 'custom_items';
}

interface CustomerInfo {
  name: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  zip: string;
}

// ─── Helpers ────────────────────────────────────────────────────────────────────

function getScreenMSRP(productType: keyof typeof PRICING_MATRICES, width: number, height: number): number | null {
  const matrix = PRICING_MATRICES[productType];
  if (!matrix) return null;
  if (width > MAX_WIDTH || height > MAX_HEIGHT) return null;
  // Minimum orderable width is 30" (matches the Min Width manufacturing rule) — no quote below.
  if (width > 0 && width < 30) return null;
  const effectiveWidth = Math.max(width, MIN_WIDTH);
  const effectiveHeight = Math.max(height, MIN_HEIGHT);
  const heightBucket = matrix.heights.find(h => h >= effectiveHeight) || matrix.heights[matrix.heights.length - 1];
  const widthBucket = matrix.widths.find(w => w >= effectiveWidth) || matrix.widths[matrix.widths.length - 1];
  const widthIdx = matrix.widths.indexOf(widthBucket);
  return matrix.prices[heightBucket as keyof typeof matrix.prices]?.[widthIdx] ?? null;
}

function getRemotePrice(remoteType: string, qty: number, isDealer: boolean): number {
  if (qty <= 0 || remoteType === 'none') return 0;
  const prices = isDealer ? REMOTE_PRICING.DEALER : REMOTE_PRICING.MSRP;
  const unitPrice = prices[remoteType] ?? prices['alpha_pioneer_1ch'];
  return unitPrice * qty;
}

function calculatePackaging(prePackSubtotal: number, qty: number): number {
  // Full rate; large-order packaging discount removed 2026-09-18.
  return 82.5 * qty;
}

function fmt(n: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
}

// ─── Component ──────────────────────────────────────────────────────────────────

export function PriceBuilderPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  // ── Customer Info ─────────────────────────────────────────────────────────────
  const [customerInfo, setCustomerInfo] = useState<CustomerInfo>({ name: '', email: '', phone: '', address: '', city: '', state: '', zip: '' });
  const [projectName, setProjectName] = useState('');

  // ── Fraction input mode ──────────────────────────────────────────────────────
  const [fractionMode, setFractionMode] = useState(() => {
    return localStorage.getItem('fractionMode') === 'true';
  });
  useEffect(() => {
    localStorage.setItem('fractionMode', fractionMode ? 'true' : 'false');
  }, [fractionMode]);

  // ── Keder Screens Section ─────────────────────────────────────────────────────
  const [productType, setProductType] = useState<keyof typeof PRICING_MATRICES>('solar94');
  const [fabricType, setFabricType] = useState<string>('omega_pro_5');
  const [fabricColor, setFabricColor] = useState<string>('Black');
  const [screens, setScreens] = useState<ScreenItem[]>([{ id: 1, width: 0, height: 0, uChannel: false, windowPanel: false, windowCount: 1 }]);
  const [motorPackage, setMotorPackage] = useState<'alpha' | 'somfy' | 'keder' | 'none'>('keder');
  const [remoteType, setRemoteType] = useState('none');
  const [remoteQty, setRemoteQty] = useState(0);
  const [wallSwitchType, setWallSwitchType] = useState('none');
  const [wallSwitchQty, setWallSwitchQty] = useState(0);

  // ── Custom Line Items ─────────────────────────────────────────────────────────
  const [customItems, setCustomItems] = useState<CustomLineItem[]>([]);
  const [catalogProducts, setCatalogProducts] = useState<CustomProduct[]>([]);
  const [showCatalogModal, setShowCatalogModal] = useState(false);
  const [showAddProductModal, setShowAddProductModal] = useState(false);

  // New catalog product form
  const [newProduct, setNewProduct] = useState({ name: '', category: 'Other', description: '', unitCost: 0, msrpPrice: 0, sku: '' });

  // ── Discounts ─────────────────────────────────────────────────────────────────
  const [discounts, setDiscounts] = useState<DiscountItem[]>([]);

  // ── Linked Order ─────────────────────────────────────────────────────────────
  const [linkedOrderId, setLinkedOrderId] = useState<string | null>(null);
  const [linkedOrderNumber, setLinkedOrderNumber] = useState<string | null>(null);

  // ── Additional Charges ────────────────────────────────────────────────────────
  const [salesTax, setSalesTax] = useState(0);
  const [installationCost, setInstallationCost] = useState(0);
  const [shippingCost, setShippingCost] = useState(0);

  // ── Freight Quote (live Freightview) ─────────────────────────────────────────
  const [freightDestZip, setFreightDestZip] = useState('');
  const [freightLiftgate, setFreightLiftgate] = useState(false);
  const [freightResidential, setFreightResidential] = useState(false);
  const [freightLoading, setFreightLoading] = useState(false);
  const [freightError, setFreightError] = useState<string | null>(null);
  const [freightQuote, setFreightQuote] = useState<{
    carrier?: string;
    service?: string;
    transitTime?: string;
    rate?: number;
    fallback?: boolean;
  } | null>(null);

  const handleGetFreightQuote = async () => {
    setFreightError(null);
    setFreightQuote(null);
    if (!freightDestZip || freightDestZip.length !== 5) {
      setFreightError('Enter a 5-digit ZIP');
      return;
    }
    const validScreens = screens.filter(s => s.width > 0 && s.height > 0);
    if (validScreens.length === 0) {
      setFreightError('Add at least one screen with dimensions');
      return;
    }
    setFreightLoading(true);
    try {
      // Each screen ships in its own box/skid (no pallets): 12"W × 14"H,
      // L = longest dim + 10". Weight = L(ft) × 14.5 lb/ft (BOL-calibrated).
      const boxes = validScreens.map(s => {
        const len = Math.round(Math.max(s.width, s.height) + 10);
        return { length: len, width: 12, height: 14, weight: Math.round((len / 12) * 14.5) };
      });
      const length = Math.max(...boxes.map(b => b.length));
      const height = 14;
      const weight = boxes.reduce((sum, b) => sum + b.weight, 0);
      const pallets = boxes.length;

      const result = await freightApi.estimate({
        destination_zip: freightDestZip,
        length,
        width: 12,
        height,
        weight,
        pallets,
        boxes,
        freight_class: 85,
        accessorials: {
          liftgate: freightLiftgate,
          residential: freightResidential,
          inside: false,
        },
      });

      if ((result as any).success) {
        setFreightQuote({
          carrier: (result as any).carrier,
          service: (result as any).service,
          transitTime: (result as any).transitTime,
          rate: (result as any).rate,
          fallback: (result as any).fallback,
        });
        // Auto-fill the Shipping ($) field so the rest of the calculation flows.
        if ((result as any).rate) setShippingCost((result as any).rate);
      } else {
        setFreightError((result as any).error || 'Unable to get freight quote');
      }
    } catch (err: any) {
      console.error('Freight error:', err);
      setFreightError('Error getting freight quote. Please try again.');
    } finally {
      setFreightLoading(false);
    }
  };

  // ── Quote tracking ──────────────────────────────────────────────────────────
  const [activeQuoteId, setActiveQuoteId] = useState<string | null>(null);
  // createdAt of the currently-loaded saved quote — drives the $45 wall-switch
  // grandfather for quotes still within their 30-day validity. Null = fresh quote.
  const [loadedQuoteCreatedAt, setLoadedQuoteCreatedAt] = useState<string | null>(null);

  // ── Save state ────────────────────────────────────────────────────────────────
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedMessage, setSavedMessage] = useState('');

  // ── Email state ───────────────────────────────────────────────────────────────
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailNotes, setEmailNotes] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);

  // ── Pricing display preference ─────────────────────────────────────────────────
  const [pricingDisplay, setPricingDisplay] = useState<'both' | 'dealer_only' | 'msrp_only'>('both');

  // ── Price multiplier (price increase + tariff + firm adjustment) ──────────────
  const [priceMultiplier, setPriceMultiplier] = useState(1);
  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    fetch(`${import.meta.env.VITE_API_URL || '/api'}/system-settings/pricing`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then(res => res.json())
      .then(data => {
        const pi = data.price_increase_percent || 0;
        const tariff = data.tariff_percent || 0;
        const firmAdj = data.firm_price_adjustment_percent || 0;
        setPriceMultiplier((1 + pi / 100) * (1 + tariff / 100) * (1 + firmAdj / 100));
      })
      .catch(err => console.error('Failed to fetch pricing settings:', err));
  }, []);

  // ── Active section tab ────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState<'screens' | 'custom' | 'discounts' | 'shipping'>('screens');

  const dealerDiscount = useMemo(() => {
    const u = user as any;
    return u?.discountPercent || u?.discount_percent || u?.discountRate || 0;
  }, [user]);

  // Load catalog products
  useEffect(() => {
    customProductsApi.list().then(setCatalogProducts).catch(() => {});
  }, []);

  // Load pricing display preference when user is available
  const dealerId = (user as any)?.dealerId;
  useEffect(() => {
    if (!dealerId) return;
    const token = localStorage.getItem('auth_token');
    const apiUrl = import.meta.env.VITE_API_URL || '/api';
    fetch(`${apiUrl}/dealers/${dealerId}/quote-defaults`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(r => r.ok ? r.json() : null).then(d => {
      if (d?.pricingDisplay) setPricingDisplay(d.pricingDisplay);
    }).catch(() => {});
  }, [dealerId]);

  // Load a saved quote into the form
  const loadQuoteIntoForm = useCallback((q: SavedQuote) => {
    const cfg = q.configuration as any;
    setProjectName(q.name || '');
    setProductType((cfg.productType || 'solar94') as keyof typeof PRICING_MATRICES);
    if (cfg.fabricType) setFabricType(cfg.fabricType as string);
    setFabricColor(cfg.fabricColor || 'Black');
    setMotorPackage(cfg.motorPackage || 'keder');
    setRemoteType(cfg.remoteType || 'none');
    setRemoteQty(cfg.remoteQty ?? (cfg.extraRemote ? 2 : 0));
    setWallSwitchType(cfg.wallSwitchType || 'none');
    setWallSwitchQty(cfg.wallSwitchQty || 0);
    setScreens((cfg.screens || []).map((s: any, i: number) => ({
      id: i + 1,
      width: s.width || 0,
      height: s.height || 0,
      uChannel: s.uChannel || false,
      windowPanel: s.windowPanel || false,
      windowCount: s.windowCount || 1,
      customerPrice: undefined,
    })));
    if (cfg.customerInfo) setCustomerInfo(cfg.customerInfo);
    if (cfg.customItems) setCustomItems(cfg.customItems.map((item: any, i: number) => ({ ...item, id: item.id || i + 1 })));
    if (cfg.discountsConfig) setDiscounts(cfg.discountsConfig.map((d: any, i: number) => ({ ...d, id: d.id || i + 1 })));
    setSalesTax(cfg.salesTax || 0);
    setInstallationCost(cfg.installationCost || 0);
    setShippingCost(cfg.shippingCost || 0);
    if (cfg.linkedOrderId) setLinkedOrderId(cfg.linkedOrderId);
    if (cfg.linkedOrderNumber) setLinkedOrderNumber(cfg.linkedOrderNumber);
    setActiveQuoteId(q.id);
    setLoadedQuoteCreatedAt(q.createdAt || null);
  }, []);

  // Load quote by ID if redirected from Quick Estimate page
  useEffect(() => {
    const quoteId = sessionStorage.getItem('loadQuoteId');
    if (quoteId) {
      sessionStorage.removeItem('loadQuoteId');
      quotesApi.get(quoteId).then(q => loadQuoteIntoForm(q)).catch(() => {});
    }
  }, [loadQuoteIntoForm]);

  // Load lead data if navigated from Leads page
  useEffect(() => {
    const leadData = sessionStorage.getItem('leadToPriceBuilder');
    if (leadData) {
      try {
        const lead = JSON.parse(leadData);
        sessionStorage.removeItem('leadToPriceBuilder');
        setCustomerInfo({
          name: lead.customerName || '',
          email: lead.customerEmail || '',
          phone: lead.customerPhone || '',
          address: lead.customerAddress || '',
          city: lead.customerCity || '',
          state: lead.customerState || '',
          zip: lead.customerZip || '',
        });
        if (lead.projectName) setProjectName(lead.projectName);
      } catch (err) {
        console.error('Failed to load lead data:', err);
      }
    }
  }, []);

  // Load order data if navigated from Order Detail page
  useEffect(() => {
    const orderData = sessionStorage.getItem('orderToPriceBuilder');
    if (orderData) {
      try {
        const data = JSON.parse(orderData);
        sessionStorage.removeItem('orderToPriceBuilder');
        const orderId = data.orderId;
        const orderNumber = data.orderNumber;
        if (orderId) setLinkedOrderId(orderId);
        if (orderNumber) setLinkedOrderNumber(orderNumber);

        // Helper to apply order screens
        const applyOrderScreens = (orderScreens: any[]) => {
          if (orderScreens?.length > 0) {
            setScreens(orderScreens.map((s: any, i: number) => ({
              id: i + 1,
              width: s.width || 0,
              height: s.height || 0,
              uChannel: s.uChannel || false,
            })));
          }
        };

        // Immediately pre-fill from order data (don't wait for async API call)
        if (data.customerInfo) {
          setCustomerInfo(prev => ({
            ...prev,
            name: data.customerInfo.name || prev.name,
            email: data.customerInfo.email || prev.email,
            phone: data.customerInfo.phone || prev.phone,
          }));
        }
        applyOrderScreens(data.screens);
        if (orderNumber) setProjectName(`Order ${orderNumber}`);

        // If no screens were passed, fetch from API
        const screensPromise = (!data.screens || data.screens.length === 0) && orderId
          ? ordersApi.getItems(orderId).then((items: any[]) => {
              if (items?.length > 0) {
                const fetched = items.map((item: any) => ({
                  width: Number(item.widthTop || item.width_top) || 0,
                  height: Number(item.heightLeft || item.height_left) || 0,
                  uChannel: (item.uChannel || item.u_channel || 'none') !== 'none',
                })).filter((s: any) => s.width > 0 && s.height > 0);
                if (fetched.length > 0) applyOrderScreens(fetched);
                return fetched;
              }
              return [];
            }).catch(() => [])
          : Promise.resolve(data.screens || []);

        // Then check for existing saved estimate (will override with saved data if found)
        Promise.all([
          quotesApi.list(1, 50).catch(() => ({ data: [] })),
          screensPromise,
        ]).then(([res, _fetchedScreens]) => {
          const existing = (res.data || []).find((q: any) => {
            const cfg = q.configuration as any;
            return cfg?.linkedOrderId === orderId;
          });
          if (existing) {
            // Load saved estimate but keep order's screens if estimate has none
            const cfg = existing.configuration as any;
            if (cfg?.screens?.length > 0) {
              loadQuoteIntoForm(existing);
            } else {
              // Load everything except screens from the saved estimate
              setProjectName(existing.name || `Order ${orderNumber}`);
              setProductType((cfg?.productType || 'solar94') as keyof typeof PRICING_MATRICES);
              if (cfg?.fabricType) setFabricType(cfg.fabricType as string);
              setFabricColor(cfg?.fabricColor || 'Black');
              setMotorPackage(cfg?.motorPackage || 'keder');
              setRemoteType(cfg?.remoteType || 'none');
              setRemoteQty(cfg?.remoteQty ?? (cfg?.extraRemote ? 2 : 0));
              setWallSwitchType(cfg?.wallSwitchType || 'none');
              setWallSwitchQty(cfg?.wallSwitchQty || 0);
              if (cfg?.customerInfo) setCustomerInfo(cfg.customerInfo);
              if (cfg?.customItems) setCustomItems(cfg.customItems.map((item: any, i: number) => ({ ...item, id: item.id || i + 1 })));
              if (cfg?.discountsConfig) setDiscounts(cfg.discountsConfig.map((d: any, i: number) => ({ ...d, id: d.id || i + 1 })));
              setSalesTax(cfg?.salesTax || 0);
              setInstallationCost(cfg?.installationCost || 0);
              setShippingCost(cfg?.shippingCost || 0);
              setActiveQuoteId(existing.id);
            }
          }
        }).catch(() => {});
      } catch (err) {
        console.error('Failed to load order data for Price Builder:', err);
      }
    }
  }, [loadQuoteIntoForm]);

  // ── Screen helpers ────────────────────────────────────────────────────────────

  const addScreen = () => {
    const maxId = Math.max(...screens.map(s => s.id), 0);
    setScreens([...screens, { id: maxId + 1, width: 0, height: 0, uChannel: false, windowPanel: false, windowCount: 1 }]);
  };

  const removeScreen = (id: number) => {
    if (screens.length <= 1) return;
    setScreens(screens.filter(s => s.id !== id));
  };

  const updateScreen = (id: number, updates: Partial<ScreenItem>) => {
    setScreens(screens.map(s => s.id === id ? { ...s, ...updates } : s));
  };

  const duplicateScreen = (id: number) => {
    const screen = screens.find(s => s.id === id);
    if (!screen) return;
    const maxId = Math.max(...screens.map(s => s.id), 0);
    setScreens([...screens, { ...screen, id: maxId + 1 }]);
  };

  // ── Custom item helpers ───────────────────────────────────────────────────────

  const addCustomItem = (fromCatalog?: CustomProduct) => {
    const maxId = Math.max(...customItems.map(i => i.id), 0, 0);
    if (fromCatalog) {
      setCustomItems([...customItems, {
        id: maxId + 1,
        name: fromCatalog.name,
        category: fromCatalog.category,
        description: fromCatalog.description || '',
        quantity: 1,
        unitCost: fromCatalog.unitCost,
        customerPrice: fromCatalog.msrpPrice,
        catalogProductId: fromCatalog.id,
      }]);
    } else {
      setCustomItems([...customItems, {
        id: maxId + 1,
        name: '',
        category: 'Other',
        description: '',
        quantity: 1,
        unitCost: 0,
        customerPrice: 0,
      }]);
    }
  };

  const removeCustomItem = (id: number) => {
    setCustomItems(customItems.filter(i => i.id !== id));
  };

  const updateCustomItem = (id: number, updates: Partial<CustomLineItem>) => {
    setCustomItems(customItems.map(i => i.id === id ? { ...i, ...updates } : i));
  };

  // ── Discount helpers ──────────────────────────────────────────────────────────

  const addDiscount = () => {
    const maxId = Math.max(...discounts.map(d => d.id), 0);
    setDiscounts([...discounts, { id: maxId + 1, name: '', type: 'percent', value: 0, appliesTo: 'all' }]);
  };

  const removeDiscount = (id: number) => {
    setDiscounts(discounts.filter(d => d.id !== id));
  };

  const updateDiscount = (id: number, updates: Partial<DiscountItem>) => {
    setDiscounts(discounts.map(d => d.id === id ? { ...d, ...updates } : d));
  };

  // ── Calculations ──────────────────────────────────────────────────────────────

  const quote = useMemo(() => {
    // Keder screens
    const screenLineItems = screens.map((s, idx) => {
      // Business rule: any screen with a vinyl window panel uses the CVW matrix,
      // regardless of fabric. (Solar fabric + window = CVW pricing.)
      const effectiveCategory: keyof typeof PRICING_MATRICES = s.windowPanel ? 'cvw' : productType;
      const rawMsrp = (s.width > 0 && s.height > 0) ? getScreenMSRP(effectiveCategory, s.width, s.height) : null;
      const msrp = rawMsrp !== null ? rawMsrp * priceMultiplier : null;
      const dealerPrice = msrp ? msrp * (1 - dealerDiscount / 100) : null;
      const uChannelFeet = s.uChannel ? (s.height / 12) * 2 : 0;
      const uChannelMSRP = uChannelFeet * UCHANNEL_RATES.MSRP;
      const uChannelDealer = uChannelFeet * UCHANNEL_RATES.DEALER;
      // Window panel: first window included in screen price; +$175 per additional
      // window — flat-priced, no dealer discount applied (matches NewOrderPage
      // which adds the $175 raw to the dealer total).
      const windowCount = s.windowPanel ? Math.max(1, s.windowCount || 1) : 0;
      const extraWindowMSRP = windowCount > 0 ? Math.max(0, windowCount - 1) * EXTRA_WINDOW_PRICE : 0;
      const extraWindowDealer = extraWindowMSRP;
      const customerPrice = s.customerPrice ?? msrp;

      return {
        number: idx + 1,
        width: s.width,
        height: s.height,
        msrpPrice: msrp,
        dealerPrice,
        customerPrice,
        uChannelFeet,
        uChannelMSRP,
        uChannelDealer,
        windowCount,
        extraWindowMSRP,
        extraWindowDealer,
        isValid: msrp !== null && s.width > 0 && s.height > 0,
        isOverMax: s.width > MAX_WIDTH || s.height > MAX_HEIGHT,
        isBelowMinWidth: s.width > 0 && s.width < MIN_BUILD_WIDTH,
      };
    });

    const validScreens = screenLineItems.filter(s => s.isValid);
    const screenCount = validScreens.length;

    const screensMSRP = validScreens.reduce((sum, s) => sum + (s.msrpPrice || 0), 0);
    const screensDealer = validScreens.reduce((sum, s) => sum + (s.dealerPrice || 0), 0);
    const screensCustomerTotal = validScreens.reduce((sum, s) => sum + (s.customerPrice || s.msrpPrice || 0), 0);

    const uChannelMSRP = validScreens.reduce((sum, s) => sum + s.uChannelMSRP, 0);
    const uChannelDealer = validScreens.reduce((sum, s) => sum + s.uChannelDealer, 0);
    const uChannelFeet = validScreens.reduce((sum, s) => sum + s.uChannelFeet, 0);

    // Extra window panels (first per screen included; +$175 each after)
    const extraWindowsMSRP = validScreens.reduce((sum, s) => sum + s.extraWindowMSRP, 0);
    const extraWindowsDealer = validScreens.reduce((sum, s) => sum + s.extraWindowDealer, 0);
    const extraWindowsCount = validScreens.reduce((sum, s) => sum + Math.max(0, s.windowCount - 1), 0);

    const remoteMSRP = screenCount > 0 ? getRemotePrice(remoteType, remoteQty, false) : 0;
    const remoteDealer = screenCount > 0 ? getRemotePrice(remoteType, remoteQty, true) : 0;

    // Wall switches
    const wallSwitchTotal = wallSwitchQty > 0 ? wallSwitchPriceForQuote(loadedQuoteCreatedAt) * wallSwitchQty : 0;

    // Somfy upgrade ($275/unit)
    const somfyUpgrade = motorPackage === 'somfy' ? SOMFY_UPGRADE_PER_UNIT * screenCount : 0;
    // Alpha upgrade ($125/unit) — Keder is the default
    const alphaUpgrade = motorPackage === 'alpha' ? ALPHA_UPGRADE_PER_UNIT * screenCount : 0;
    const motorUpgrade = somfyUpgrade + alphaUpgrade;

    const kederSubtotalMSRP = screensMSRP + uChannelMSRP + extraWindowsMSRP + remoteMSRP + wallSwitchTotal + motorUpgrade;
    const kederSubtotalDealer = screensDealer + uChannelDealer + extraWindowsDealer + remoteDealer + wallSwitchTotal + motorUpgrade;
    const kederSubtotalCustomer = screensCustomerTotal + uChannelMSRP + extraWindowsMSRP + remoteMSRP + wallSwitchTotal + motorUpgrade;

    const packaging = calculatePackaging(kederSubtotalDealer, screenCount);

    // Custom line items
    const customItemsTotal = customItems.reduce((sum, item) => sum + (item.customerPrice * item.quantity), 0);
    const customItemsCost = customItems.reduce((sum, item) => sum + (item.unitCost * item.quantity), 0);

    // Subtotal before discounts
    const subtotalCustomer = kederSubtotalCustomer + packaging + customItemsTotal;
    const subtotalDealer = kederSubtotalDealer + packaging + customItemsCost;

    // Apply discounts
    let totalDiscountAmount = 0;
    for (const d of discounts) {
      if (d.value <= 0) continue;
      let base = 0;
      if (d.appliesTo === 'all') base = subtotalCustomer;
      else if (d.appliesTo === 'screens') base = kederSubtotalCustomer;
      else if (d.appliesTo === 'custom_items') base = customItemsTotal;

      if (d.type === 'percent') totalDiscountAmount += base * (d.value / 100);
      else if (d.type === 'fixed') totalDiscountAmount += d.value;
      else if (d.type === 'per_screen') totalDiscountAmount += d.value * screenCount;
    }

    const afterDiscountCustomer = subtotalCustomer - totalDiscountAmount;

    // Tax, installation, shipping
    const taxAmount = afterDiscountCustomer * (salesTax / 100);
    const installTotal = installationCost * Math.max(screenCount, 1);

    const grandTotalCustomer = afterDiscountCustomer + taxAmount + installTotal + shippingCost;
    // subtotalDealer ALREADY includes packaging (see subtotalDealer above) — do
    // NOT add it again here or "Your Cost" double-counts packaging (was inflating
    // dealer cost by the packaging amount and understating profit).
    const grandTotalDealer = subtotalDealer;

    // Profit = PRODUCT margin only: goods revenue (after discount) minus the
    // dealer's goods cost. Excludes tax (remitted to the state — not profit),
    // installation (the dealer's own labor/service, cost not tracked here), and
    // shipping (pass-through; the freight the dealer charges the customer is not
    // captured net of what they pay Keder). afterDiscountCustomer already
    // excludes tax/install/shipping, so subtracting dealer cost gives true margin.
    const profit = afterDiscountCustomer - grandTotalDealer;
    const marginPercent = afterDiscountCustomer > 0 ? (profit / afterDiscountCustomer) * 100 : 0;

    return {
      screenLineItems,
      screenCount,
      screensMSRP, screensDealer, screensCustomerTotal,
      uChannelMSRP, uChannelDealer, uChannelFeet,
      extraWindowsMSRP, extraWindowsDealer, extraWindowsCount,
      remoteMSRP, remoteDealer,
      wallSwitchTotal, somfyUpgrade, alphaUpgrade,
      kederSubtotalMSRP, kederSubtotalDealer, kederSubtotalCustomer,
      packaging,
      customItemsTotal, customItemsCost,
      subtotalCustomer, subtotalDealer,
      totalDiscountAmount,
      afterDiscountCustomer,
      taxAmount,
      installTotal,
      shippingCost,
      grandTotalCustomer, grandTotalDealer,
      profit, marginPercent,
    };
  }, [screens, productType, motorPackage, remoteType, remoteQty, wallSwitchQty, customItems, discounts, dealerDiscount, salesTax, installationCost, shippingCost, priceMultiplier, loadedQuoteCreatedAt]);

  // Screens entered below the 30" buildable floor — block the estimate, like New Order.
  const belowMinWidthNumbers = screens
    .map((s, i) => (s.width > 0 && s.width < MIN_BUILD_WIDTH ? i + 1 : 0))
    .filter(Boolean);
  const belowMinWidthError = belowMinWidthNumbers.length
    ? `Screen #${belowMinWidthNumbers[0]} width must be at least 30". We don't build screens narrower than 30 inches.`
    : null;

  // ── Save Estimate ─────────────────────────────────────────────────────────────

  const handleSave = async () => {
    if (belowMinWidthError) {
      setSaveError(belowMinWidthError);
      return;
    }
    const name = projectName || `Estimate - ${new Date().toLocaleDateString()}`;
    setSaving(true);
    setSaveError(null);
    const payload = {
      name,
      configuration: {
        productType,
        fabricType,
        fabricColor,
        screens: screens.map(s => ({
          width: s.width,
          height: s.height,
          uChannel: s.uChannel,
          windowPanel: s.windowPanel || false,
          windowCount: s.windowCount || 1,
        })),
        motorPackage,
        remoteType,
        remoteQty,
        wallSwitchType,
        wallSwitchQty,
        extraRemote: remoteQty > 1, // backward compat
        dealerDiscount,
        customerInfo,
        customItems,
        discountsConfig: discounts,
        salesTax,
        installationCost,
        shippingCost,
        linkedOrderId: linkedOrderId || undefined,
        linkedOrderNumber: linkedOrderNumber || undefined,
      } as any,
      pricing: {
        screensMSRP: quote.screensMSRP,
        screensDealer: quote.screensDealer,
        remoteMSRP: quote.remoteMSRP,
        remoteDealer: quote.remoteDealer,
        wallSwitchTotal: quote.wallSwitchTotal,
        somfyUpgrade: quote.somfyUpgrade,
        alphaUpgrade: quote.alphaUpgrade,
        uChannelMSRP: quote.uChannelMSRP,
        uChannelDealer: quote.uChannelDealer,
        uChannelFeet: quote.uChannelFeet,
        packaging: quote.packaging,
        grandTotalMSRP: quote.grandTotalCustomer,
        grandTotalDealer: quote.grandTotalDealer,
        customItemsTotal: quote.customItemsTotal,
        totalDiscountAmount: quote.totalDiscountAmount,
        profit: quote.profit,
      } as any,
    };
    try {
      let saved: SavedQuote;
      if (activeQuoteId) {
        saved = await quotesApi.update(activeQuoteId, payload);
      } else {
        saved = await quotesApi.create(payload);
      }
      const wasNew = !activeQuoteId;
      setActiveQuoteId(saved.id);
      // Write customer sale total back to the linked order
      if (linkedOrderId && quote.grandTotalCustomer > 0) {
        try {
          await ordersApi.update(linkedOrderId, { customerSellPrice: quote.grandTotalCustomer });
        } catch { /* non-critical */ }
      }
      setSavedMessage(wasNew ? 'Estimate saved!' : 'Estimate updated!');
      setTimeout(() => setSavedMessage(''), 5000);
    } catch (err: any) {
      setSaveError(err?.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // ── Push to Order ──────────────────────────────────────────────────────────────

  const handlePushToOrder = () => {
    if (belowMinWidthError) { setSaveError(belowMinWidthError); return; }
    const validScreens = screens.filter(s => s.width > 0 && s.height > 0);
    sessionStorage.setItem('priceBuilderToOrder', JSON.stringify({
      customerInfo,
      productType,
      motorPackage,
      remoteType,
      remoteQty,
      wallSwitchType,
      wallSwitchQty,
      extraRemote: remoteQty > 1, // backward compat
      fabricColor,
      screens: validScreens.map(s => ({
        width: s.width,
        height: s.height,
        uChannel: s.uChannel,
        windowPanel: s.windowPanel || false,
        windowCount: s.windowCount || 1,
      })),
      // Pricing breakdown for commission auto-calculation
      pricing: {
        kederScreensOnly: quote.kederSubtotalCustomer + quote.packaging,
        customItemsTotal: quote.customItemsTotal,
        installTotal: quote.installTotal,
        shippingCost: quote.shippingCost,
        totalDiscountAmount: quote.totalDiscountAmount,
        grandTotalCustomer: quote.grandTotalCustomer,
      },
    }));
    navigate('/orders/new');
  };

  // ── Print / PDF ───────────────────────────────────────────────────────────────

  const handlePrint = () => {
    const dealerName = (user as any)?.dealerName || (user as any)?.companyName || 'Keder Screens';
    const logoUrl = (user as any)?.logoUrl;

    const screenRows = quote.screenLineItems.filter(s => s.isValid).map(s => {
      const winLabel = s.windowCount > 0
        ? ` <span style="color:#6b7280;font-size:11px;">+ ${s.windowCount} vinyl window${s.windowCount === 1 ? '' : 's'}${s.extraWindowMSRP > 0 ? ` (+${fmt(s.extraWindowMSRP)})` : ''}</span>`
        : '';
      return `<tr>
        <td style="padding:8px;border-bottom:1px solid #e5e7eb">${PRODUCT_LABELS[productType]} Screen #${s.number} <span style="color:#6b7280;font-size:11px;">— ${fabricColor}</span>${winLabel}</td>
        <td style="padding:8px;border-bottom:1px solid #e5e7eb;text-align:center">${s.width}" x ${s.height}"</td>
        <td style="padding:8px;border-bottom:1px solid #e5e7eb;text-align:center">1</td>
        <td style="padding:8px;border-bottom:1px solid #e5e7eb;text-align:right">${fmt((s.customerPrice || s.msrpPrice || 0) + s.extraWindowMSRP)}</td>
      </tr>`;
    }).join('');

    const customRows = customItems.map(item =>
      `<tr>
        <td style="padding:8px;border-bottom:1px solid #e5e7eb">${item.name}${item.description ? `<br><span style="font-size:12px;color:#6b7280">${item.description}</span>` : ''}</td>
        <td style="padding:8px;border-bottom:1px solid #e5e7eb;text-align:center">${item.category}</td>
        <td style="padding:8px;border-bottom:1px solid #e5e7eb;text-align:center">${item.quantity}</td>
        <td style="padding:8px;border-bottom:1px solid #e5e7eb;text-align:right">${fmt(item.customerPrice * item.quantity)}</td>
      </tr>`
    ).join('');

    const discountRows = discounts.filter(d => d.value > 0).map(d => {
      const label = d.type === 'percent' ? `${d.value}%` : d.type === 'per_screen' ? `${fmt(d.value)}/screen` : fmt(d.value);
      return `<tr><td colspan="3" style="padding:4px 8px;color:#dc2626">${d.name || 'Discount'} (${label})</td>
        <td style="padding:4px 8px;text-align:right;color:#dc2626">-${fmt(
          d.type === 'percent' ? (d.appliesTo === 'screens' ? quote.kederSubtotalCustomer : d.appliesTo === 'custom_items' ? quote.customItemsTotal : quote.subtotalCustomer) * d.value / 100
          : d.type === 'per_screen' ? d.value * quote.screenCount : d.value
        )}</td></tr>`;
    }).join('');

    const html = `<!DOCTYPE html><html><head><title>Estimate - ${projectName || dealerName}</title>
    <style>body{font-family:Arial,sans-serif;max-width:800px;margin:0 auto;padding:40px;color:#1f2937}
    table{width:100%;border-collapse:collapse}th{background:#f9fafb;padding:8px;text-align:left;border-bottom:2px solid #e5e7eb}
    .total-row td{font-weight:bold;font-size:18px;border-top:2px solid #1f2937;padding-top:12px}
    @media print{body{padding:20px}}</style></head><body>
    <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:30px;border-bottom:3px solid #eab308;padding-bottom:20px">
      <div>${logoUrl ? `<img src="${logoUrl}" style="max-height:60px;max-width:200px" />` : `<h1 style="margin:0;font-size:24px">${dealerName}</h1>`}</div>
      <div style="text-align:right"><h2 style="margin:0;color:#4b5563">ESTIMATE</h2>
        <p style="margin:4px 0;color:#6b7280">${new Date().toLocaleDateString()}</p>
        ${projectName ? `<p style="margin:4px 0;font-weight:bold">${projectName}</p>` : ''}
      </div>
    </div>

    ${customerInfo.name ? `<div style="margin-bottom:24px;background:#f9fafb;padding:16px;border-radius:8px">
      <h3 style="margin:0 0 8px;color:#4b5563">Prepared For</h3>
      <p style="margin:0;font-weight:bold">${customerInfo.name}</p>
      ${customerInfo.email ? `<p style="margin:2px 0;color:#6b7280">${customerInfo.email}</p>` : ''}
      ${customerInfo.phone ? `<p style="margin:2px 0;color:#6b7280">${customerInfo.phone}</p>` : ''}
      ${customerInfo.address ? `<p style="margin:2px 0;color:#6b7280">${customerInfo.address}</p>` : ''}
    </div>` : ''}

    <table>
      <thead><tr><th>Item</th><th style="text-align:center">Details</th><th style="text-align:center">Qty</th><th style="text-align:right">Price</th></tr></thead>
      <tbody>
        ${screenRows}
        ${quote.uChannelFeet > 0 ? `<tr><td style="padding:8px;border-bottom:1px solid #e5e7eb">U-Channel</td><td style="padding:8px;border-bottom:1px solid #e5e7eb;text-align:center">${quote.uChannelFeet.toFixed(1)} ft</td><td style="padding:8px;border-bottom:1px solid #e5e7eb;text-align:center">-</td><td style="padding:8px;border-bottom:1px solid #e5e7eb;text-align:right">${fmt(quote.uChannelMSRP)}</td></tr>` : ''}
        ${remoteQty > 0 ? `<tr><td style="padding:8px;border-bottom:1px solid #e5e7eb">Remote${remoteType !== 'none' ? ` (${remotesForPackage(motorPackage).find(r => r.value === remoteType)?.label || remoteType})` : ` <span style="color:#b91c1c;font-size:11px;">— no remote type selected</span>`}</td><td style="padding:8px;border-bottom:1px solid #e5e7eb;text-align:center">-</td><td style="padding:8px;border-bottom:1px solid #e5e7eb;text-align:center">${remoteQty}</td><td style="padding:8px;border-bottom:1px solid #e5e7eb;text-align:right">${fmt(quote.remoteMSRP)}</td></tr>` : ''}
        ${quote.wallSwitchTotal > 0 ? `<tr><td style="padding:8px;border-bottom:1px solid #e5e7eb">Wall Switch</td><td style="padding:8px;border-bottom:1px solid #e5e7eb;text-align:center">-</td><td style="padding:8px;border-bottom:1px solid #e5e7eb;text-align:center">${wallSwitchQty}</td><td style="padding:8px;border-bottom:1px solid #e5e7eb;text-align:right">${fmt(quote.wallSwitchTotal)}</td></tr>` : ''}
        ${quote.somfyUpgrade > 0 ? `<tr><td style="padding:8px;border-bottom:1px solid #e5e7eb">Somfy Motor Upgrade</td><td style="padding:8px;border-bottom:1px solid #e5e7eb;text-align:center">-</td><td style="padding:8px;border-bottom:1px solid #e5e7eb;text-align:center">${quote.screenCount}</td><td style="padding:8px;border-bottom:1px solid #e5e7eb;text-align:right">${fmt(quote.somfyUpgrade)}</td></tr>` : ''}
        ${quote.alphaUpgrade > 0 ? `<tr><td style="padding:8px;border-bottom:1px solid #e5e7eb">Alpha Motor Upgrade</td><td style="padding:8px;border-bottom:1px solid #e5e7eb;text-align:center">-</td><td style="padding:8px;border-bottom:1px solid #e5e7eb;text-align:center">${quote.screenCount}</td><td style="padding:8px;border-bottom:1px solid #e5e7eb;text-align:right">${fmt(quote.alphaUpgrade)}</td></tr>` : ''}
        ${quote.packaging > 0 ? `<tr><td style="padding:8px;border-bottom:1px solid #e5e7eb">Packaging</td><td style="padding:8px;border-bottom:1px solid #e5e7eb;text-align:center">-</td><td style="padding:8px;border-bottom:1px solid #e5e7eb;text-align:center">-</td><td style="padding:8px;border-bottom:1px solid #e5e7eb;text-align:right">${fmt(quote.packaging)}</td></tr>` : ''}
        ${customRows}
        ${discountRows}
      </tbody>
    </table>

    <div style="margin-top:16px;text-align:right">
      <table style="width:300px;margin-left:auto">
        <tr><td style="padding:4px 8px">Subtotal</td><td style="padding:4px 8px;text-align:right">${fmt(quote.afterDiscountCustomer)}</td></tr>
        ${quote.taxAmount > 0 ? `<tr><td style="padding:4px 8px">Tax (${salesTax}%)</td><td style="padding:4px 8px;text-align:right">${fmt(quote.taxAmount)}</td></tr>` : ''}
        ${quote.installTotal > 0 ? `<tr><td style="padding:4px 8px">Installation</td><td style="padding:4px 8px;text-align:right">${fmt(quote.installTotal)}</td></tr>` : ''}
        ${shippingCost > 0 ? `<tr><td style="padding:4px 8px">Shipping</td><td style="padding:4px 8px;text-align:right">${fmt(shippingCost)}</td></tr>` : ''}
        <tr class="total-row"><td style="padding:8px">Total</td><td style="padding:8px;text-align:right;color:#eab308">${fmt(quote.grandTotalCustomer)}</td></tr>
      </table>
    </div>

    <div style="margin-top:40px;padding-top:20px;border-top:1px solid #e5e7eb">
      <p style="background:#fff7ed;border:1px solid #fb923c;padding:10px 14px;border-radius:6px;color:#9a3412;font-size:13px;text-align:center">
        <strong>This estimate is valid for 30 days &mdash; through ${(() => { const d = new Date(); d.setDate(d.getDate() + 30); return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }); })()}.</strong>
        Orders placed after this date are subject to current pricing.
      </p>
      <p style="text-align:center;color:#9ca3af;font-size:12px;margin-top:12px">${dealerName}</p>
    </div>
    </body></html>`;

    // Render into a hidden iframe and print from there. window.open('', '_blank')
    // is frequently popup-blocked — when it was, the handle was null and the
    // function silently returned, so "Print / Save PDF" appeared to do nothing.
    // An iframe needs no popup permission and prints reliably (incl. Save as PDF).
    const iframe = document.createElement('iframe');
    iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
    document.body.appendChild(iframe);
    const doc = iframe.contentWindow?.document;
    if (!doc) { document.body.removeChild(iframe); return; }
    doc.open();
    doc.write(html);
    doc.close();
    const triggerPrint = () => {
      try {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
      } finally {
        setTimeout(() => { try { document.body.removeChild(iframe); } catch { /* already removed */ } }, 1000);
      }
    };
    // Wait for the logo image (if present) so it isn't missing from the PDF.
    const img = doc.querySelector('img');
    if (img && !img.complete) {
      img.addEventListener('load', () => setTimeout(triggerPrint, 100));
      img.addEventListener('error', () => setTimeout(triggerPrint, 100));
      setTimeout(triggerPrint, 1500); // fallback if the image never resolves
    } else {
      setTimeout(triggerPrint, 200);
    }
  };

  // ── Email Estimate ────────────────────────────────────────────────────────────

  const handleSendEmail = async () => {
    if (!customerInfo.email || !customerInfo.name) return;
    setEmailSending(true);
    setEmailError(null);
    try {
      const screenData = quote.screenLineItems.filter(s => s.isValid).map(s => ({
        index: s.number,
        width: s.width,
        height: s.height,
        productType: PRODUCT_LABELS[productType],
        msrpPrice: s.customerPrice || s.msrpPrice || 0,
      }));

      // Build the full add-on / adjustment line list so the email mirrors the
      // on-screen Estimate Summary exactly (every non-screen line item). Missing
      // any of these — U-Channel was the reported case — makes the emailed line
      // items fail to reconcile to the subtotal/total.
      const extras: Array<{ label: string; amount: number }> = [];
      if (quote.uChannelFeet > 0)
        extras.push({ label: `U-Channel (${quote.uChannelFeet.toFixed(1)} ft @ $12.75/ft)`, amount: quote.uChannelMSRP });
      if (quote.extraWindowsCount > 0)
        extras.push({ label: `Extra Vinyl Windows (${quote.extraWindowsCount} × $${EXTRA_WINDOW_PRICE})`, amount: quote.extraWindowsMSRP });
      if (quote.remoteMSRP > 0) {
        const remoteLabel = remoteType !== 'none'
          ? `${(motorPackage === 'alpha' ? ALPHA_REMOTES : motorPackage === 'somfy' ? SOMFY_REMOTES : KEDER_REMOTES).find(r => r.value === remoteType)?.label || 'Remote'} x${remoteQty}`
          : `Remote${remoteQty > 1 ? ` x${remoteQty}` : ''}`;
        extras.push({ label: remoteLabel, amount: quote.remoteMSRP });
      }
      if (quote.wallSwitchTotal > 0)
        extras.push({ label: `Wall Switch x${wallSwitchQty}`, amount: quote.wallSwitchTotal });
      if (quote.somfyUpgrade > 0)
        extras.push({ label: `Somfy Upgrade ($275 x ${quote.screenCount})`, amount: quote.somfyUpgrade });
      if (quote.alphaUpgrade > 0)
        extras.push({ label: `Alpha Upgrade ($125 x ${quote.screenCount})`, amount: quote.alphaUpgrade });
      if (quote.packaging > 0)
        extras.push({ label: 'Packaging', amount: quote.packaging });
      customItems.filter(i => i.customerPrice > 0).forEach(item =>
        extras.push({ label: `${item.name || 'Custom Item'}${item.quantity > 1 ? ` x${item.quantity}` : ''}`, amount: item.customerPrice * item.quantity }));
      // Discounts — itemize each (matches the quote calc's per-discount base).
      discounts.forEach(d => {
        if (d.value <= 0) return;
        const base = d.appliesTo === 'all' ? quote.subtotalCustomer
          : d.appliesTo === 'screens' ? quote.kederSubtotalCustomer
          : quote.customItemsTotal;
        const amt = d.type === 'percent' ? base * (d.value / 100)
          : d.type === 'fixed' ? d.value
          : d.value * quote.screenCount;
        if (amt <= 0) return;
        const label = `${d.name || 'Discount'}${d.type === 'percent' ? ` (${d.value}%)` : ''}`;
        extras.push({ label, amount: -amt });
      });
      if (quote.taxAmount > 0)
        extras.push({ label: `Tax (${salesTax}%)`, amount: quote.taxAmount });
      if (quote.installTotal > 0)
        extras.push({ label: 'Installation', amount: quote.installTotal });
      if (shippingCost > 0)
        extras.push({ label: 'Shipping', amount: shippingCost });

      await quotesApi.sendEstimate({
        customerEmail: customerInfo.email,
        customerName: customerInfo.name,
        screens: screenData,
        extras: extras.length > 0 ? extras : undefined,
        subtotal: quote.subtotalCustomer,
        totalMsrp: quote.grandTotalCustomer,
        notes: emailNotes || undefined,
      });
      setEmailSent(true);
      setTimeout(() => { setEmailSent(false); setShowEmailModal(false); }, 2000);
    } catch (err: any) {
      setEmailError(err?.response?.data?.message || 'Failed to send');
    } finally {
      setEmailSending(false);
    }
  };

  // ── Save catalog product ──────────────────────────────────────────────────────

  const handleSaveCatalogProduct = async () => {
    if (!newProduct.name || !newProduct.msrpPrice) return;
    try {
      const saved = await customProductsApi.create({
        name: newProduct.name,
        category: newProduct.category,
        description: newProduct.description,
        unitCost: newProduct.unitCost,
        msrpPrice: newProduct.msrpPrice,
        sku: newProduct.sku,
      });
      setCatalogProducts([...catalogProducts, saved]);
      setNewProduct({ name: '', category: 'Other', description: '', unitCost: 0, msrpPrice: 0, sku: '' });
      setShowAddProductModal(false);
    } catch (err) {
      console.error('Failed to save product:', err);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <PageHeader
          title="Price Builder"
          subtitle={linkedOrderNumber ? `Estimate for Order ${linkedOrderNumber}` : 'Build detailed customer estimates with custom line items and discounts'}
        />
        {linkedOrderId && (
          <button
            onClick={() => navigate(`/orders/${linkedOrderId}`)}
            className="flex items-center gap-2 px-4 py-2 bg-keder-gray text-white rounded-lg hover:bg-gray-600 transition-colors text-sm font-medium"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
            Back to Order
          </button>
        )}
      </div>

      {/* Customer Info + Project Name */}
      <div className="card p-4">
        <div className="flex items-center gap-2 mb-3">
          <svg className="w-5 h-5 text-keder-yellow" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
          <h3 className="text-lg font-semibold text-white">Customer & Project</h3>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          <input
            type="text"
            placeholder="Project Name"
            value={projectName}
            onChange={e => setProjectName(e.target.value)}
            className="bg-keder-dark border border-keder-gray rounded-lg px-3 py-2 text-white placeholder-gray-500 focus:border-keder-yellow focus:outline-none"
          />
          <input
            type="text"
            placeholder="Customer Name"
            value={customerInfo.name}
            onChange={e => setCustomerInfo({ ...customerInfo, name: e.target.value })}
            className="bg-keder-dark border border-keder-gray rounded-lg px-3 py-2 text-white placeholder-gray-500 focus:border-keder-yellow focus:outline-none"
          />
          <input
            type="email"
            placeholder="Email"
            value={customerInfo.email}
            onChange={e => setCustomerInfo({ ...customerInfo, email: e.target.value })}
            className="bg-keder-dark border border-keder-gray rounded-lg px-3 py-2 text-white placeholder-gray-500 focus:border-keder-yellow focus:outline-none"
          />
          <input
            type="tel"
            placeholder="Phone"
            value={customerInfo.phone}
            onChange={e => setCustomerInfo({ ...customerInfo, phone: e.target.value })}
            className="bg-keder-dark border border-keder-gray rounded-lg px-3 py-2 text-white placeholder-gray-500 focus:border-keder-yellow focus:outline-none"
          />
          <input
            type="text"
            placeholder="Address"
            value={customerInfo.address}
            onChange={e => setCustomerInfo({ ...customerInfo, address: e.target.value })}
            className="bg-keder-dark border border-keder-gray rounded-lg px-3 py-2 text-white placeholder-gray-500 focus:border-keder-yellow focus:outline-none"
          />
          <input
            type="text"
            placeholder="City"
            value={customerInfo.city}
            onChange={e => setCustomerInfo({ ...customerInfo, city: e.target.value })}
            className="bg-keder-dark border border-keder-gray rounded-lg px-3 py-2 text-white placeholder-gray-500 focus:border-keder-yellow focus:outline-none"
          />
          <input
            type="text"
            placeholder="State"
            value={customerInfo.state}
            onChange={e => setCustomerInfo({ ...customerInfo, state: e.target.value })}
            className="bg-keder-dark border border-keder-gray rounded-lg px-3 py-2 text-white placeholder-gray-500 focus:border-keder-yellow focus:outline-none"
          />
          <input
            type="text"
            placeholder="ZIP"
            value={customerInfo.zip}
            onChange={e => setCustomerInfo({ ...customerInfo, zip: e.target.value })}
            className="bg-keder-dark border border-keder-gray rounded-lg px-3 py-2 text-white placeholder-gray-500 focus:border-keder-yellow focus:outline-none"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Configuration (2 cols) */}
        <div className="lg:col-span-2 space-y-4">

          {/* Tab Navigation */}
          <div className="flex gap-1 bg-keder-dark border border-keder-gray rounded-lg p-1">
            {[
              { key: 'screens' as const, label: 'Keder Screens', count: screens.filter(s => s.width > 0 && s.height > 0).length },
              { key: 'custom' as const, label: 'Custom Items', count: customItems.length },
              { key: 'discounts' as const, label: 'Discounts & Fees', count: discounts.length },
              { key: 'shipping' as const, label: 'Shipping & Tax', count: shippingCost > 0 || salesTax > 0 || installationCost > 0 ? 1 : 0 },
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={clsx(
                  'flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors',
                  activeTab === tab.key
                    ? 'bg-keder-yellow text-black'
                    : 'text-gray-400 hover:text-white hover:bg-keder-gray/50'
                )}
              >
                {tab.label} {tab.count > 0 && <span className="ml-1 text-xs">({tab.count})</span>}
              </button>
            ))}
          </div>

          {/* ─── SCREENS TAB ─────────────────────────────────────────────── */}
          {activeTab === 'screens' && (
            <div className="space-y-4">
              {/* Fabric Type + Color (Product Type is set automatically from fabric) */}
              <div className="card p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Fabric</label>
                    <select
                      value={fabricType}
                      onChange={e => {
                        const v = e.target.value;
                        const fabric = FABRIC_FLAT.find(f => f.value === v);
                        if (!fabric) return;
                        setFabricType(v);
                        setProductType(fabric.pricingCategory);
                        const colors = COLORS_BY_FABRIC[v] || ['Black'];
                        if (!colors.includes(fabricColor)) setFabricColor(colors[0]);
                      }}
                      className="input-field w-full text-sm"
                    >
                      {FABRIC_TYPE_GROUPS.map(g => (
                        <optgroup key={g.group} label={g.group}>
                          {g.options.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                    <p className="mt-1 text-[11px] text-gray-500 italic">
                      Pricing matrix: <span className="text-gray-400">{PRODUCT_LABELS[productType]}</span>
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Fabric Color</label>
                    <select
                      value={fabricColor}
                      onChange={e => setFabricColor(e.target.value)}
                      className="input-field w-full text-sm"
                    >
                      {(COLORS_BY_FABRIC[fabricType] || ['Black']).map(c => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                    <p className="mt-1 text-[11px] text-gray-500 italic">Color is cosmetic — doesn't change price.</p>
                  </div>
                </div>
              </div>

              {/* Motor Package & Controls */}
              <div className="card p-4">
                <label className="block text-sm text-gray-400 mb-2">Motor Package</label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
                  {MOTOR_PACKAGES.map(pkg => {
                    const isSelected = motorPackage === pkg.value;
                    return (
                      <button
                        key={pkg.value}
                        type="button"
                        onClick={() => {
                          const nextPkg = pkg.value as string;
                          setMotorPackage(nextPkg as any);
                          if (nextPkg === 'none') {
                            setRemoteType('none');
                            setWallSwitchType('none');
                            setRemoteQty(0);
                            setWallSwitchQty(0);
                          } else {
                            // Don't silently wipe a selection the dealer already made —
                            // carry it over when the new package offers it.
                            setRemoteType(prev => carryOverOption(prev, remotesForPackage(nextPkg)));
                            setWallSwitchType(prev => carryOverOption(prev, wallSwitchesForPackage(nextPkg)));
                          }
                        }}
                        className={clsx(
                          'relative p-2.5 rounded-lg border-2 text-left transition-all',
                          isSelected
                            ? 'border-keder-yellow bg-keder-yellow/10'
                            : 'border-gray-600 hover:border-gray-500 bg-keder-darker'
                        )}
                      >
                        {pkg.badge && (
                          <span className="absolute -top-2 -right-2 px-1.5 py-0.5 bg-purple-600 text-white text-[10px] rounded-full">
                            {pkg.badge}
                          </span>
                        )}
                        <div className="font-medium text-white text-sm">{pkg.label}</div>
                        <div className="text-[11px] text-gray-400">{pkg.description}</div>
                        {isSelected && (
                          <div className="absolute top-1.5 right-1.5">
                            <svg className="w-4 h-4 text-keder-yellow" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>

                {motorPackage === 'somfy' && (
                  <div className="mb-3 p-2 bg-blue-900/30 border border-blue-500 rounded text-xs text-blue-200">
                    <strong>Somfy Upgrade:</strong> $275 per screen unit added automatically.
                  </div>
                )}

                {motorPackage !== 'none' && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Remote Type</label>
                      <select
                        value={remoteType}
                        onChange={(e) => setRemoteType(e.target.value)}
                        className="input-field w-full text-sm"
                      >
                        {(motorPackage === 'alpha' ? ALPHA_REMOTES
                          : motorPackage === 'somfy' ? SOMFY_REMOTES
                          : KEDER_REMOTES
                        ).map(opt => (
                          <option key={opt.value} value={opt.value} disabled={(opt as any).disabled}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Remote QTY</label>
                      <input
                        type="number"
                        value={remoteQty}
                        onChange={(e) => setRemoteQty(parseInt(e.target.value) || 0)}
                        onWheel={(e) => e.currentTarget.blur()}
                        className="input-field w-full text-sm"
                        min={0}
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Wall Switch</label>
                      <select
                        value={wallSwitchType}
                        onChange={(e) => setWallSwitchType(e.target.value)}
                        className="input-field w-full text-sm"
                      >
                        {(motorPackage === 'alpha' ? ALPHA_WALL_SWITCHES
                          : motorPackage === 'somfy' ? SOMFY_WALL_SWITCHES
                          : KEDER_WALL_SWITCHES
                        ).map(opt => (
                          <option key={opt.value} value={opt.value} disabled={(opt as any).disabled}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Wall Switch QTY</label>
                      <input
                        type="number"
                        value={wallSwitchQty}
                        onChange={(e) => setWallSwitchQty(parseInt(e.target.value) || 0)}
                        onWheel={(e) => e.currentTarget.blur()}
                        className="input-field w-full text-sm"
                        min={0}
                      />
                    </div>
                  </div>
                )}

                {/* Quantity with no type selected prices at $0 and would otherwise
                    be easy to miss on the printed estimate. Call it out here. */}
                {motorPackage !== 'none' && remoteQty > 0 && remoteType === 'none' && (
                  <div className="mt-3 p-2.5 rounded-lg bg-amber-900/30 border border-amber-500 text-sm text-amber-200">
                    <strong>Pick a Remote Type.</strong> You have a remote quantity of {remoteQty} but no
                    type selected, so the remotes price at $0 and will show as unpriced on the estimate.
                  </div>
                )}
              </div>

              {/* Fraction/Decimal Toggle */}
              <div className="flex items-center justify-end gap-2 mb-2">
                <span className="text-xs text-gray-500">Input mode:</span>
                <button
                  type="button"
                  onClick={() => setFractionMode(!fractionMode)}
                  className={clsx(
                    'px-3 py-1 text-xs font-semibold rounded-full transition-colors',
                    fractionMode
                      ? 'bg-keder-yellow text-black'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  )}
                >
                  {fractionMode ? '⅝ Fractions' : '.125 Decimal'}
                </button>
              </div>

              {/* Screen List */}
              {screens.map((screen, idx) => {
                const li = quote.screenLineItems[idx];
                return (
                  <div key={screen.id} className="card p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-semibold text-gray-300">Screen #{idx + 1}</h4>
                      <div className="flex items-center gap-1">
                        <button onClick={() => duplicateScreen(screen.id)} className="px-2 py-1 text-xs text-gray-400 hover:text-keder-yellow border border-gray-600 hover:border-keder-yellow rounded transition-colors" title="Duplicate">
                          Duplicate
                        </button>
                        {screens.length > 1 && (
                          <button onClick={() => removeScreen(screen.id)} className="p-1 text-gray-400 hover:text-red-400" title="Remove">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Width (in)</label>
                        <DimensionInput
                          value={screen.width ? String(screen.width) : ''}
                          fractionMode={fractionMode}
                          onChange={(val) => updateScreen(screen.id, { width: parseFloat(val) || 0 })}
                          placeholder={fractionMode ? 'e.g. 96 5/16' : 'Width'}
                          className="w-full bg-keder-dark border border-keder-gray rounded-lg px-3 py-2 text-white focus:border-keder-yellow focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Height (in)</label>
                        <DimensionInput
                          value={screen.height ? String(screen.height) : ''}
                          fractionMode={fractionMode}
                          onChange={(val) => updateScreen(screen.id, { height: parseFloat(val) || 0 })}
                          placeholder={fractionMode ? 'e.g. 72 1/2' : 'Height'}
                          className="w-full bg-keder-dark border border-keder-gray rounded-lg px-3 py-2 text-white focus:border-keder-yellow focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Customer Price ($)</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          max="999999"
                          value={screen.customerPrice ?? li?.msrpPrice ?? ''}
                          onChange={e => {
                            const val = parseFloat(e.target.value);
                            updateScreen(screen.id, { customerPrice: val > 0 ? Math.min(val, 999999) : undefined });
                          }}
                          placeholder="MSRP"
                          className="w-full bg-keder-dark border border-keder-gray rounded-lg px-3 py-2 text-white focus:border-keder-yellow focus:outline-none"
                        />
                      </div>
                      <div className="flex items-end gap-2">
                        <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer pb-2">
                          <input
                            type="checkbox"
                            checked={screen.uChannel}
                            onChange={e => updateScreen(screen.id, { uChannel: e.target.checked })}
                            className="rounded border-gray-500 bg-keder-dark text-keder-yellow focus:ring-keder-yellow"
                          />
                          U-Channel
                        </label>
                      </div>
                    </div>
                    {/* Window panel row */}
                    <div className="mt-3 flex items-center gap-4 flex-wrap">
                      <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={!!screen.windowPanel}
                          onChange={e => updateScreen(screen.id, {
                            windowPanel: e.target.checked,
                            windowCount: e.target.checked ? Math.max(1, screen.windowCount || 1) : 1,
                          })}
                          className="rounded border-gray-500 bg-keder-dark text-keder-yellow focus:ring-keder-yellow"
                        />
                        Vinyl Window Panel
                      </label>
                      {screen.windowPanel && (
                        <>
                          <div className="flex items-center gap-2">
                            <label className="text-xs text-gray-500">Windows</label>
                            <input
                              type="number"
                              min={1}
                              value={screen.windowCount || 1}
                              onChange={e => updateScreen(screen.id, { windowCount: Math.max(1, parseInt(e.target.value) || 1) })}
                              onWheel={(e) => e.currentTarget.blur()}
                              className="w-16 input-field text-sm text-center"
                            />
                          </div>
                          <span className="text-[11px] text-gray-500 italic">
                            First window included. {Math.max(0, (screen.windowCount || 1) - 1) > 0 && (
                              <span className="text-gray-400">+{Math.max(0, (screen.windowCount || 1) - 1)} extra × ${EXTRA_WINDOW_PRICE} = {fmt(Math.max(0, (screen.windowCount || 1) - 1) * EXTRA_WINDOW_PRICE)}</span>
                            )}
                          </span>
                        </>
                      )}
                    </div>
                    {li?.isValid && (
                      <div className="mt-2 flex items-center gap-4 text-xs text-gray-400 flex-wrap">
                        {pricingDisplay !== 'dealer_only' && <span>MSRP: {fmt(li.msrpPrice || 0)}</span>}
                        {pricingDisplay !== 'msrp_only' && <span>Your Cost: {fmt(li.dealerPrice || 0)}</span>}
                        {pricingDisplay !== 'dealer_only' && li.customerPrice !== li.msrpPrice && <span className="text-keder-yellow">Customer: {fmt(li.customerPrice || 0)}</span>}
                        {pricingDisplay !== 'msrp_only' && li.uChannelFeet > 0 && <span>+ U-Channel: {fmt(li.uChannelDealer)}</span>}
                        {pricingDisplay !== 'msrp_only' && li.extraWindowMSRP > 0 && <span>+ Extra Windows: {fmt(li.extraWindowMSRP)}</span>}
                      </div>
                    )}
                    {li?.isOverMax && <p className="mt-2 text-xs text-red-400">Exceeds max dimensions — custom quote required</p>}
                    {li?.isBelowMinWidth && (
                      <p className="mt-2 text-xs text-red-400">Width must be at least 30". We don't build screens narrower than 30 inches.</p>
                    )}
                  </div>
                );
              })}
              <button onClick={addScreen} className="w-full py-2 border-2 border-dashed border-keder-gray rounded-lg text-gray-400 hover:text-keder-yellow hover:border-keder-yellow transition-colors">
                + Add Screen
              </button>
            </div>
          )}

          {/* ─── CUSTOM ITEMS TAB ────────────────────────────────────────── */}
          {activeTab === 'custom' && (
            <div className="space-y-4">
              <div className="flex gap-2 flex-wrap">
                <button onClick={() => addCustomItem()} className="px-4 py-2 bg-keder-yellow text-black rounded-lg text-sm font-medium hover:bg-yellow-400 transition-colors">
                  + Custom Item
                </button>
                {catalogProducts.length > 0 && (
                  <button onClick={() => setShowCatalogModal(true)} className="px-4 py-2 bg-keder-gray text-white rounded-lg text-sm font-medium hover:bg-gray-600 transition-colors">
                    From Catalog ({catalogProducts.length})
                  </button>
                )}
                <button onClick={() => setShowAddProductModal(true)} className="px-4 py-2 border border-keder-gray text-gray-300 rounded-lg text-sm font-medium hover:border-keder-yellow hover:text-keder-yellow transition-colors">
                  + Save to Catalog
                </button>
              </div>

              {customItems.length === 0 && (
                <div className="card p-8 text-center text-gray-500">
                  <p>No custom items yet. Add third-party products, services, or extras.</p>
                  <p className="text-sm mt-1">TVs, heaters, pools, interior screens, installation packages, etc.</p>
                </div>
              )}

              {customItems.map((item, idx) => (
                <div key={item.id} className="card p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-semibold text-gray-300">
                      {item.name || `Item #${idx + 1}`}
                      {item.catalogProductId && <span className="ml-2 text-xs text-keder-yellow">(from catalog)</span>}
                    </h4>
                    <button onClick={() => removeCustomItem(item.id)} className="p-1 text-gray-400 hover:text-red-400">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    <div className="col-span-2 md:col-span-1">
                      <label className="block text-xs text-gray-500 mb-1">Name</label>
                      <input
                        type="text"
                        value={item.name}
                        onChange={e => updateCustomItem(item.id, { name: e.target.value })}
                        placeholder="Product name"
                        className="w-full bg-keder-dark border border-keder-gray rounded-lg px-3 py-2 text-white text-sm focus:border-keder-yellow focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Category</label>
                      <select
                        value={item.category}
                        onChange={e => updateCustomItem(item.id, { category: e.target.value })}
                        className="w-full bg-keder-dark border border-keder-gray rounded-lg px-3 py-2 text-white text-sm focus:border-keder-yellow focus:outline-none"
                      >
                        {SUGGESTED_CATEGORIES.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Qty</label>
                      <input
                        type="number"
                        min="1"
                        value={item.quantity}
                        onChange={e => updateCustomItem(item.id, { quantity: parseInt(e.target.value) || 1 })}
                        className="w-full bg-keder-dark border border-keder-gray rounded-lg px-3 py-2 text-white text-sm focus:border-keder-yellow focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Your Cost ($)</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={item.unitCost || ''}
                        onChange={e => updateCustomItem(item.id, { unitCost: parseFloat(e.target.value) || 0 })}
                        className="w-full bg-keder-dark border border-keder-gray rounded-lg px-3 py-2 text-white text-sm focus:border-keder-yellow focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Customer Price ($)</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={item.customerPrice || ''}
                        onChange={e => updateCustomItem(item.id, { customerPrice: parseFloat(e.target.value) || 0 })}
                        className="w-full bg-keder-dark border border-keder-gray rounded-lg px-3 py-2 text-white text-sm focus:border-keder-yellow focus:outline-none"
                      />
                    </div>
                  </div>
                  <div className="mt-2">
                    <input
                      type="text"
                      value={item.description}
                      onChange={e => updateCustomItem(item.id, { description: e.target.value })}
                      placeholder="Description (optional)"
                      className="w-full bg-keder-dark border border-keder-gray rounded-lg px-3 py-2 text-white text-sm focus:border-keder-yellow focus:outline-none"
                    />
                  </div>
                  {item.customerPrice > 0 && item.unitCost > 0 && (
                    <div className="mt-2 text-xs text-gray-400">
                      Margin: {fmt(item.customerPrice - item.unitCost)} ({((item.customerPrice - item.unitCost) / item.customerPrice * 100).toFixed(1)}%) per unit
                      {item.quantity > 1 && ` | Line total: ${fmt(item.customerPrice * item.quantity)}`}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* ─── DISCOUNTS & FEES TAB ────────────────────────────────────── */}
          {activeTab === 'discounts' && (
            <div className="space-y-4">
              {/* Discounts */}
              <div className="card p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-semibold text-white">Discounts</h3>
                  <button onClick={addDiscount} className="px-3 py-1 bg-keder-yellow text-black rounded text-sm font-medium hover:bg-yellow-400 transition-colors">
                    + Add Discount
                  </button>
                </div>
                {discounts.length === 0 && (
                  <p className="text-gray-500 text-sm py-2">No discounts applied. Add percentage, fixed, or per-screen discounts.</p>
                )}
                {discounts.map((d, idx) => (
                  <div key={d.id} className="flex flex-wrap items-end gap-3 py-3 border-b border-keder-gray last:border-0">
                    <div className="flex-1 min-w-[120px]">
                      <label className="block text-xs text-gray-500 mb-1">Label</label>
                      <input
                        type="text"
                        value={d.name}
                        onChange={e => updateDiscount(d.id, { name: e.target.value })}
                        placeholder="e.g. Bundle Deal"
                        className="w-full bg-keder-dark border border-keder-gray rounded-lg px-3 py-2 text-white text-sm focus:border-keder-yellow focus:outline-none"
                      />
                    </div>
                    <div className="w-[130px]">
                      <label className="block text-xs text-gray-500 mb-1">Type</label>
                      <select
                        value={d.type}
                        onChange={e => updateDiscount(d.id, { type: e.target.value as any })}
                        className="w-full bg-keder-dark border border-keder-gray rounded-lg px-3 py-2 text-white text-sm focus:border-keder-yellow focus:outline-none"
                      >
                        <option value="percent">Percentage (%)</option>
                        <option value="fixed">Fixed Amount ($)</option>
                        <option value="per_screen">Per Screen ($)</option>
                      </select>
                    </div>
                    <div className="w-[100px]">
                      <label className="block text-xs text-gray-500 mb-1">Value</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={d.value || ''}
                        onChange={e => updateDiscount(d.id, { value: parseFloat(e.target.value) || 0 })}
                        className="w-full bg-keder-dark border border-keder-gray rounded-lg px-3 py-2 text-white text-sm focus:border-keder-yellow focus:outline-none"
                      />
                    </div>
                    <div className="w-[140px]">
                      <label className="block text-xs text-gray-500 mb-1">Applies To</label>
                      <select
                        value={d.appliesTo}
                        onChange={e => updateDiscount(d.id, { appliesTo: e.target.value as any })}
                        className="w-full bg-keder-dark border border-keder-gray rounded-lg px-3 py-2 text-white text-sm focus:border-keder-yellow focus:outline-none"
                      >
                        <option value="all">Everything</option>
                        <option value="screens">Screens Only</option>
                        <option value="custom_items">Custom Items Only</option>
                      </select>
                    </div>
                    <button onClick={() => removeDiscount(d.id)} className="p-2 text-gray-400 hover:text-red-400">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                  </div>
                ))}
              </div>

            </div>
          )}

          {/* ─── SHIPPING & TAX TAB ──────────────────────────────────────── */}
          {activeTab === 'shipping' && (
            <div className="space-y-4">
              {/* Live Freight Quote — talks to Freightview, auto-fills the
                  Shipping ($) field below. Optional; dealers can still type
                  shipping by hand if they don't want a live quote. */}
              <div className="card p-4">
                <h3 className="text-lg font-semibold text-white mb-3">Freight Quote (LTL)</h3>
                <ShippingDisclaimer className="mb-3" />
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
                  <div className="md:col-span-1">
                    <label className="block text-xs text-gray-500 mb-1">Destination ZIP</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={5}
                      value={freightDestZip}
                      onChange={e => setFreightDestZip(e.target.value.replace(/\D/g, '').slice(0, 5))}
                      placeholder="e.g. 78704"
                      className="w-full bg-keder-dark border border-keder-gray rounded-lg px-3 py-2 text-white focus:border-keder-yellow focus:outline-none"
                    />
                  </div>
                  <div className="md:col-span-2 flex items-center gap-4">
                    <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={freightLiftgate}
                        onChange={e => setFreightLiftgate(e.target.checked)}
                        className="rounded border-keder-gray bg-keder-dark"
                      />
                      Liftgate
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={freightResidential}
                        onChange={e => setFreightResidential(e.target.checked)}
                        className="rounded border-keder-gray bg-keder-dark"
                      />
                      Residential
                    </label>
                  </div>
                  <button
                    type="button"
                    onClick={handleGetFreightQuote}
                    disabled={freightLoading || !freightDestZip || freightDestZip.length !== 5}
                    className="bg-keder-yellow text-black font-semibold rounded-lg px-4 py-2 hover:bg-yellow-400 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {freightLoading ? 'Getting quote…' : 'Get Quote'}
                  </button>
                </div>
                {freightError && (
                  <p className="text-sm text-red-400 mt-3">{freightError}</p>
                )}
                {freightQuote && freightQuote.rate != null && (
                  <div className="mt-3 p-3 bg-keder-dark/60 border border-keder-yellow/40 rounded-lg flex items-center justify-between">
                    <div className="text-sm">
                      <div className="text-white font-semibold">
                        {freightQuote.carrier || 'Carrier'}{freightQuote.service ? ` — ${freightQuote.service}` : ''}
                      </div>
                      <div className="text-xs text-gray-400">
                        {freightQuote.transitTime ? `${freightQuote.transitTime} transit` : ''}
                        {freightQuote.fallback && <span className="ml-2 text-yellow-400">(fallback estimate)</span>}
                      </div>
                    </div>
                    <div className="text-keder-yellow text-xl font-bold">
                      ${freightQuote.rate.toFixed(2)}
                    </div>
                  </div>
                )}
                <p className="text-xs text-gray-500 mt-2">
                  Live quote pulled from Freightview using your screens' dimensions + count. Auto-fills the Shipping field below — you can override it.
                </p>
              </div>

              {/* Tax / Installation / Shipping manual fields */}
              <div className="card p-4">
                <h3 className="text-lg font-semibold text-white mb-3">Tax, Installation & Shipping</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Sales Tax (%)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={salesTax || ''}
                      onChange={e => setSalesTax(parseFloat(e.target.value) || 0)}
                      placeholder="0"
                      className="w-full bg-keder-dark border border-keder-gray rounded-lg px-3 py-2 text-white focus:border-keder-yellow focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Installation ($/unit)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={installationCost || ''}
                      onChange={e => setInstallationCost(parseFloat(e.target.value) || 0)}
                      placeholder="0"
                      className="w-full bg-keder-dark border border-keder-gray rounded-lg px-3 py-2 text-white focus:border-keder-yellow focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Shipping ($)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={shippingCost || ''}
                      onChange={e => setShippingCost(parseFloat(e.target.value) || 0)}
                      placeholder="0"
                      className="w-full bg-keder-dark border border-keder-gray rounded-lg px-3 py-2 text-white focus:border-keder-yellow focus:outline-none"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Right: Summary (1 col, sticky) */}
        <div className="lg:col-span-1">
          <div className="card p-4 lg:sticky lg:top-4 space-y-4">
            <h3 className="text-lg font-bold text-white border-b border-keder-gray pb-2">Estimate Summary</h3>

            {/* Screens */}
            {quote.screenCount > 0 && (
              <div className="space-y-1">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">{PRODUCT_LABELS[productType]} ({quote.screenCount})</span>
                  <span className="text-white">{fmt(quote.screensCustomerTotal)}</span>
                </div>
                {quote.uChannelFeet > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">U-Channel ({quote.uChannelFeet.toFixed(1)} ft @ $12.75/ft)</span>
                    <span className="text-white">{fmt(quote.uChannelMSRP)}</span>
                  </div>
                )}
                {quote.uChannelFeet > 0 && pricingDisplay !== 'msrp_only' && (
                  <div className="flex justify-between text-xs">
                    <span className="text-green-400/70">Your cost: {quote.uChannelFeet.toFixed(1)} ft @ $9.16/ft</span>
                    <span className="text-green-400/70">{fmt(quote.uChannelDealer)}</span>
                  </div>
                )}
                {quote.extraWindowsCount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Extra Vinyl Windows ({quote.extraWindowsCount} × ${EXTRA_WINDOW_PRICE})</span>
                    <span className="text-white">{fmt(quote.extraWindowsMSRP)}</span>
                  </div>
                )}
                {quote.extraWindowsCount > 0 && pricingDisplay !== 'msrp_only' && (
                  <div className="flex justify-between text-xs">
                    <span className="text-green-400/70">Your cost</span>
                    <span className="text-green-400/70">{fmt(quote.extraWindowsDealer)}</span>
                  </div>
                )}
                {quote.remoteMSRP > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">
                      {remoteType !== 'none'
                        ? `${(motorPackage === 'alpha' ? ALPHA_REMOTES : motorPackage === 'somfy' ? SOMFY_REMOTES : KEDER_REMOTES).find(r => r.value === remoteType)?.label || 'Remote'} x${remoteQty}`
                        : `Remote${remoteQty > 1 ? ` x${remoteQty}` : ''}`
                      }
                    </span>
                    <span className="text-white">{fmt(quote.remoteMSRP)}</span>
                  </div>
                )}
                {quote.wallSwitchTotal > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Wall Switch x{wallSwitchQty}</span>
                    <span className="text-white">{fmt(quote.wallSwitchTotal)}</span>
                  </div>
                )}
                {quote.somfyUpgrade > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Somfy Upgrade ($275 x {quote.screenCount})</span>
                    <span className="text-white">{fmt(quote.somfyUpgrade)}</span>
                  </div>
                )}
                {quote.alphaUpgrade > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Alpha Upgrade ($125 x {quote.screenCount})</span>
                    <span className="text-white">{fmt(quote.alphaUpgrade)}</span>
                  </div>
                )}
                {quote.packaging > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Packaging</span>
                    <span className="text-white">{fmt(quote.packaging)}</span>
                  </div>
                )}
              </div>
            )}

            {/* Custom Items */}
            {quote.customItemsTotal > 0 && (
              <div className="space-y-1 pt-2 border-t border-keder-gray/50">
                {customItems.filter(i => i.customerPrice > 0).map(item => (
                  <div key={item.id} className="flex justify-between text-sm">
                    <span className="text-gray-400 truncate mr-2">{item.name || 'Custom Item'} {item.quantity > 1 ? `x${item.quantity}` : ''}</span>
                    <span className="text-white whitespace-nowrap">{fmt(item.customerPrice * item.quantity)}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Subtotal */}
            <div className="flex justify-between text-sm pt-2 border-t border-keder-gray">
              <span className="text-gray-300 font-medium">Subtotal</span>
              <span className="text-white font-medium">{fmt(quote.subtotalCustomer)}</span>
            </div>

            {/* Discounts */}
            {quote.totalDiscountAmount > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-red-400">Discounts</span>
                <span className="text-red-400">-{fmt(quote.totalDiscountAmount)}</span>
              </div>
            )}

            {/* Tax & fees */}
            {quote.taxAmount > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Tax ({salesTax}%)</span>
                <span className="text-white">{fmt(quote.taxAmount)}</span>
              </div>
            )}
            {quote.installTotal > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Installation</span>
                <span className="text-white">{fmt(quote.installTotal)}</span>
              </div>
            )}
            {shippingCost > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Shipping</span>
                <span className="text-white">{fmt(shippingCost)}</span>
              </div>
            )}

            {/* Grand Total */}
            <div className="flex justify-between items-baseline pt-3 border-t-2 border-keder-yellow">
              <span className="text-white text-lg font-bold">Customer Total</span>
              <span className="text-keder-yellow text-2xl font-bold">{fmt(quote.grandTotalCustomer)}</span>
            </div>

            {/* Profit — hidden in msrp_only mode (customer might see screen) */}
            {pricingDisplay !== 'msrp_only' && (
            <div className="bg-green-900/20 border border-green-800/30 rounded-lg p-3">
              <div className="flex justify-between text-sm">
                <span className="text-green-400">Your Cost</span>
                <span className="text-green-400">{fmt(quote.grandTotalDealer)}</span>
              </div>
              <div className="flex justify-between text-sm mt-1">
                <span className="text-green-400 font-bold">Your Profit</span>
                <span className="text-green-400 font-bold">{fmt(quote.profit)} ({quote.marginPercent.toFixed(1)}%)</span>
              </div>
              <p className="text-[11px] text-green-500/70 mt-1">Product margin — excludes tax, installation &amp; shipping (pass-throughs)</p>
            </div>
            )}

            {/* Actions */}
            <div className="space-y-2 pt-2">
              {/* Hard block: a sub-30" screen is entered (mirrors New Order) */}
              {belowMinWidthError && (
                <div className="text-sm text-red-300 bg-red-500/10 border border-red-500/40 px-3 py-2 rounded-lg">
                  {belowMinWidthError}
                </div>
              )}
              <button
                onClick={handleSave}
                disabled={saving || quote.grandTotalCustomer === 0 || !!belowMinWidthError}
                className="w-full py-2.5 bg-keder-yellow text-black font-semibold rounded-lg hover:bg-yellow-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {saving ? 'Saving...' : activeQuoteId ? 'Update Estimate' : 'Save Estimate'}
              </button>

              <button
                onClick={handlePushToOrder}
                disabled={quote.screenCount === 0 || !!belowMinWidthError}
                className="w-full py-2.5 border-2 border-keder-yellow text-keder-yellow font-semibold rounded-lg hover:bg-keder-yellow hover:text-black disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Push to Order
              </button>

              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={handlePrint}
                  disabled={quote.grandTotalCustomer === 0}
                  className="py-2 bg-keder-gray text-white rounded-lg text-sm hover:bg-gray-600 disabled:opacity-50 transition-colors"
                >
                  Print / PDF
                </button>
                <button
                  onClick={() => setShowEmailModal(true)}
                  disabled={quote.grandTotalCustomer === 0 || !customerInfo.email}
                  className="py-2 bg-keder-gray text-white rounded-lg text-sm hover:bg-gray-600 disabled:opacity-50 transition-colors"
                  title={!customerInfo.email ? 'Enter customer email first' : ''}
                >
                  Email Estimate
                </button>
              </div>

              {saveError && <p className="text-xs text-red-400">{saveError}</p>}
              {savedMessage && (
                <div className="flex items-center gap-2 p-2 bg-green-900/30 border border-green-700/50 rounded-lg">
                  <svg className="w-4 h-4 text-green-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                  <span className="text-sm text-green-400 font-medium">{savedMessage}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ─── Catalog Modal ─────────────────────────────────────────────────────── */}
      {showCatalogModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setShowCatalogModal(false)}>
          <div className="bg-keder-dark border border-keder-gray rounded-lg max-w-lg w-full max-h-[80vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-white mb-4">Your Product Catalog</h3>
            {catalogProducts.length === 0 ? (
              <p className="text-gray-500 py-4 text-center">No saved products yet.</p>
            ) : (
              <div className="space-y-2">
                {catalogProducts.map(product => (
                  <div key={product.id} className="flex items-center justify-between p-3 bg-keder-gray/30 rounded-lg hover:bg-keder-gray/50 transition-colors">
                    <div>
                      <p className="text-white font-medium text-sm">{product.name}</p>
                      <p className="text-gray-400 text-xs">{product.category} {product.sku ? `| ${product.sku}` : ''}</p>
                      <p className="text-keder-yellow text-xs">{fmt(product.msrpPrice)}</p>
                    </div>
                    <button
                      onClick={() => { addCustomItem(product); setShowCatalogModal(false); }}
                      className="px-3 py-1 bg-keder-yellow text-black rounded text-sm font-medium hover:bg-yellow-400 transition-colors"
                    >
                      Add
                    </button>
                  </div>
                ))}
              </div>
            )}
            <button onClick={() => setShowCatalogModal(false)} className="w-full mt-4 py-2 bg-keder-gray text-white rounded-lg hover:bg-gray-600 transition-colors">
              Close
            </button>
          </div>
        </div>
      )}

      {/* ─── Add Product to Catalog Modal ──────────────────────────────────────── */}
      {showAddProductModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setShowAddProductModal(false)}>
          <div className="bg-keder-dark border border-keder-gray rounded-lg max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-white mb-4">Save Product to Catalog</h3>
            <p className="text-gray-400 text-sm mb-4">Save products you sell regularly for quick access in future estimates.</p>
            <div className="space-y-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Product Name *</label>
                <input
                  type="text"
                  value={newProduct.name}
                  onChange={e => setNewProduct({ ...newProduct, name: e.target.value })}
                  placeholder="e.g. Samsung 65in Smart TV"
                  className="w-full bg-keder-dark border border-keder-gray rounded-lg px-3 py-2 text-white text-sm focus:border-keder-yellow focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Category</label>
                <select
                  value={newProduct.category}
                  onChange={e => setNewProduct({ ...newProduct, category: e.target.value })}
                  className="w-full bg-keder-dark border border-keder-gray rounded-lg px-3 py-2 text-white text-sm focus:border-keder-yellow focus:outline-none"
                >
                  {SUGGESTED_CATEGORIES.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">SKU</label>
                <input
                  type="text"
                  value={newProduct.sku}
                  onChange={e => setNewProduct({ ...newProduct, sku: e.target.value })}
                  placeholder="Optional"
                  className="w-full bg-keder-dark border border-keder-gray rounded-lg px-3 py-2 text-white text-sm focus:border-keder-yellow focus:outline-none"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Your Cost ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={newProduct.unitCost || ''}
                    onChange={e => setNewProduct({ ...newProduct, unitCost: parseFloat(e.target.value) || 0 })}
                    className="w-full bg-keder-dark border border-keder-gray rounded-lg px-3 py-2 text-white text-sm focus:border-keder-yellow focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Customer Price ($) *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={newProduct.msrpPrice || ''}
                    onChange={e => setNewProduct({ ...newProduct, msrpPrice: parseFloat(e.target.value) || 0 })}
                    className="w-full bg-keder-dark border border-keder-gray rounded-lg px-3 py-2 text-white text-sm focus:border-keder-yellow focus:outline-none"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Description</label>
                <input
                  type="text"
                  value={newProduct.description}
                  onChange={e => setNewProduct({ ...newProduct, description: e.target.value })}
                  placeholder="Optional description"
                  className="w-full bg-keder-dark border border-keder-gray rounded-lg px-3 py-2 text-white text-sm focus:border-keder-yellow focus:outline-none"
                />
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <button onClick={() => setShowAddProductModal(false)} className="flex-1 py-2 bg-keder-gray text-white rounded-lg hover:bg-gray-600 transition-colors">
                Cancel
              </button>
              <button
                onClick={handleSaveCatalogProduct}
                disabled={!newProduct.name || !newProduct.msrpPrice}
                className="flex-1 py-2 bg-keder-yellow text-black font-semibold rounded-lg hover:bg-yellow-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Save Product
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Email Modal ───────────────────────────────────────────────────────── */}
      {showEmailModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => !emailSending && setShowEmailModal(false)}>
          <div className="bg-keder-dark border border-keder-gray rounded-lg max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-white mb-4">Email Estimate</h3>
            {emailSent ? (
              <div className="text-center py-6">
                <svg className="w-16 h-16 text-green-400 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                <p className="text-white font-semibold">Estimate sent to {customerInfo.name}!</p>
              </div>
            ) : (
              <>
                <div className="space-y-3">
                  <div className="bg-keder-gray/30 rounded-lg p-3">
                    <p className="text-white text-sm font-medium">{customerInfo.name}</p>
                    <p className="text-gray-400 text-sm">{customerInfo.email}</p>
                    <p className="text-keder-yellow text-sm font-bold mt-1">Total: {fmt(quote.grandTotalCustomer)}</p>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Notes (optional)</label>
                    <textarea
                      value={emailNotes}
                      onChange={e => setEmailNotes(e.target.value)}
                      rows={3}
                      placeholder="Add a personal note..."
                      className="w-full bg-keder-dark border border-keder-gray rounded-lg px-3 py-2 text-white text-sm focus:border-keder-yellow focus:outline-none resize-none"
                    />
                  </div>
                </div>
                {emailError && <p className="text-xs text-red-400 mt-2">{emailError}</p>}
                <div className="flex gap-2 mt-4">
                  <button onClick={() => setShowEmailModal(false)} disabled={emailSending} className="flex-1 py-2 bg-keder-gray text-white rounded-lg hover:bg-gray-600 transition-colors">
                    Cancel
                  </button>
                  <button
                    onClick={handleSendEmail}
                    disabled={emailSending}
                    className="flex-1 py-2 bg-keder-yellow text-black font-semibold rounded-lg hover:bg-yellow-400 disabled:opacity-50 transition-colors"
                  >
                    {emailSending ? 'Sending...' : 'Send Estimate'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
